import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Spinner } from "@/components/ui/Spinner";
import { Tooltip as HoverTooltip } from "@/components/ui/Tooltip";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { formatDateTime } from "@/utils/formatDate";
import {
  devMetricsApi,
  type RequestRecord,
  type CeleryTaskRecord,
  type EndpointStat,
  type TimeseriesPoint,
  type InfraPoint,
  type RealtimePoint,
  type LoadBalancerPoint,
  type LoadBalancerScope,
  type TestRunResult,
  type TestRunDetail,
  type TestRunStatus,
  type CeleryFailuresPage,
  type CollaboraStatus,
  type WebErrorLog,
  type WebLogSection,
} from "../api/devMetrics";
import { useState, useRef, useMemo, useEffect } from "react";
import { clsx } from "clsx";
import {
  ChevronDown,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  BarChart2,
  Maximize2,
  Minimize2,
  Info,
  RefreshCw,
  X,
} from "lucide-react";
import {
  ComposedChart,
  AreaChart,
  Area,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { PerfRunsSection } from "../components/PerfRunsSection";
import { CeleryTimingSection } from "../components/CeleryTimingSection";

const HOURS_OPTIONS = [
  { label: "1h", value: 1 },
  { label: "3h", value: 3 },
  { label: "8h", value: 8 },
  { label: "24h", value: 24 },
  { label: "3d", value: 72 },
  { label: "7d", value: 168 },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const d = new Date(label);
  const timeLabel = isNaN(d.getTime())
    ? label
    : `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return (
    <div className="bg-gray-900/95 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl min-w-[140px]">
      <p className="text-gray-400 mb-1.5 font-medium">{timeLabel}</p>
      {payload.map(
        (
          p: { name: string; value: number | null; color: string },
          i: number,
        ) => {
          const isPctSeries =
            typeof p.name === "string" && p.name.endsWith("%");
          const fmt =
            p.value == null
              ? "—"
              : isPctSeries
                ? `${p.value.toFixed(1)}%`
                : typeof p.value === "number" && !Number.isInteger(p.value)
                  ? p.value.toFixed(1)
                  : p.value;
          return (
            <div key={i} className="flex items-center gap-2 mb-0.5">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: p.color }}
              />
              <span className="text-gray-300">{p.name}:</span>
              <span className="text-white font-semibold tabular-nums">
                {fmt}
              </span>
            </div>
          );
        },
      )}
    </div>
  );
}

// Tooltip for scheduler health chart — reads raw (uncapped) values from the data point
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function HealthTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const d = new Date(label);
  const timeLabel = isNaN(d.getTime())
    ? label
    : `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const raw = payload[0]?.payload ?? {};
  const entries = [
    {
      name: "Drift (ms)",
      color: "#f59e0b",
      value: raw.drift_ms as number | null,
    },
    {
      name: "Shakehand (ms)",
      color: "#06b6d4",
      value: raw.shakehand_ms as number | null,
    },
    {
      name: "QGIS shakehand (ms)",
      color: "#a855f7",
      value: raw.qgis_shakehand_ms as number | null,
    },
  ];
  return (
    <div className="bg-gray-900/95 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl min-w-[140px]">
      <p className="text-gray-400 mb-1.5 font-medium">{timeLabel}</p>
      {entries.map((e, i) => (
        <div key={i} className="flex items-center gap-2 mb-0.5">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ background: e.color }}
          />
          <span className="text-gray-300">{e.name}:</span>
          <span className="text-white font-semibold tabular-nums">
            {e.value == null ? "—" : `${e.value} ms`}
          </span>
        </div>
      ))}
    </div>
  );
}

type ChartId = "traffic" | "status" | "rate" | "health" | "ecs-web" | "ecs-celery" | "ecs-qgis" | "ecs-qgis-cache" | "ecs-centrifugo" | "ecs-collabora" | "ecs-celery-beat" | "redis-engine" | "redis-conn" | "alb-centrifugo-rt" | "gg-reconnect" | "rds-cpu" | "rds-mem" | "rds-disk" | "rds-conn" | "lb-requests" | "lb-p95" | "lb-5xx" | "lb-4xx" | "lb-unhealthy" | "lb-conns";
type StatusMode = "abs" | "pct" | "err_pct";

const STATUS_MODES: { value: StatusMode; label: string; title: string }[] = [
  { value: "abs", label: "Liczby", title: "Surowe liczby requestów" },
  {
    value: "pct",
    label: "% całość",
    title: "Udział % każdego statusu w łącznym ruchu",
  },
  {
    value: "err_pct",
    label: "% błędów",
    title: "Tylko błędy — % z łącznego ruchu",
  },
];

const INFRA_PANELS = [
  { id: "ecs-web",        label: "ECS Web" },
  { id: "ecs-celery",     label: "ECS Celery" },
  { id: "ecs-qgis",       label: "QGIS Server" },
  { id: "ecs-qgis-cache", label: "QGIS Cache" },
  { id: "ecs-centrifugo", label: "Edge: GG + nginx" },
  { id: "ecs-collabora",  label: "Collabora (edytor)" },
  { id: "ecs-celery-beat", label: "Celery beat" },
  { id: "redis-engine",   label: "Redis CPU" },
  { id: "redis-conn",     label: "Redis Poł." },
  { id: "alb-centrifugo-rt", label: "ALB GG (ms)" },
  { id: "gg-reconnect",   label: "GG reconnecty" },
  { id: "rds-cpu",        label: "RDS CPU" },
  { id: "rds-mem",        label: "RDS RAM" },
  { id: "rds-disk",       label: "RDS Dysk" },
  { id: "rds-conn",       label: "RDS Połączenia" },
] as const;

const ECS_WEB_SERIES = [
  { key: "ecs_web_cpu", label: "CPU", color: "#3b82f6", dash: false },
  { key: "ecs_web_mem", label: "RAM", color: "#6366f1", dash: true },
] as const;

const ECS_CELERY_SERIES = [
  { key: "ecs_celery_cpu", label: "CPU", color: "#f59e0b", dash: false },
  { key: "ecs_celery_mem", label: "RAM", color: "#10b981", dash: true },
] as const;

const ECS_QGIS_SERIES = [
  { key: "ecs_qgis_cpu", label: "CPU", color: "#8b5cf6", dash: false },
  { key: "ecs_qgis_mem", label: "RAM", color: "#ec4899", dash: true },
] as const;

const ECS_QGIS_CACHE_SERIES = [
  { key: "ecs_qgis_cache_cpu", label: "CPU", color: "#14b8a6", dash: false },
  { key: "ecs_qgis_cache_mem", label: "RAM", color: "#0ea5e9", dash: true },
] as const;

const ECS_CENTRIFUGO_SERIES = [
  { key: "ecs_centrifugo_cpu", label: "CPU", color: "#f43f5e", dash: false },
  { key: "ecs_centrifugo_mem", label: "RAM", color: "#fb923c", dash: true },
] as const;

const ECS_COLLABORA_SERIES = [
  { key: "ecs_collabora_cpu", label: "CPU", color: "#0ea5e9", dash: false },
  { key: "ecs_collabora_mem", label: "RAM", color: "#a3e635", dash: true },
] as const;

const ECS_CELERY_BEAT_SERIES = [
  { key: "ecs_celery_beat_cpu", label: "CPU", color: "#f59e0b", dash: false },
  { key: "ecs_celery_beat_mem", label: "RAM", color: "#c084fc", dash: true },
] as const;

// Redis: EngineCPU = single command-thread saturation (the number that matters
// for GG lag); Node CPU = whole instance. Shared broker/cache/Centrifugo engine.
const REDIS_SERIES = [
  { key: "redis_engine_cpu", label: "Engine CPU", color: "#ef4444", dash: false },
  { key: "redis_cpu", label: "Node CPU", color: "#f59e0b", dash: true },
] as const;

const GG_RECONNECT_SERIES = [
  { key: "connecting", label: "Reconnecty", color: "#3b82f6" },
  { key: "disconnected", label: "Rozłączenia", color: "#f59e0b" },
  { key: "error", label: "Błędy", color: "#ef4444" },
  { key: "resubscribe", label: "Resub.", color: "#a855f7" },
] as const;

// ALB scopes: the load balancer as a whole + each target group (traffic path).
// Colours are stable per scope so a scope reads the same across every LB chart.
const LB_SCOPE_META: { key: LoadBalancerScope; label: string; color: string }[] = [
  { key: "alb", label: "ALB (całość)", color: "#e5e7eb" },
  { key: "web", label: "Web (API)", color: "#3b82f6" },
  { key: "nginx", label: "Nginx (front)", color: "#22c55e" },
  { key: "qgis_cache", label: "QGIS cache", color: "#14b8a6" },
  { key: "centrifugo", label: "Centrifugo (GG)", color: "#a855f7" },
  { key: "collabora", label: "Collabora (edytor)", color: "#0ea5e9" },
];

// Pivot the flat (ts, scope, ...) rows into one row per ts with a column per
// scope for a single metric field, so recharts can draw one line per scope.
function pivotLbByScope(
  points: LoadBalancerPoint[],
  field: keyof LoadBalancerPoint,
): Record<string, number | null | string>[] {
  const byTs = new Map<string, Record<string, number | null | string>>();
  for (const p of points) {
    let row = byTs.get(p.ts);
    if (!row) {
      row = { ts: p.ts };
      byTs.set(p.ts, row);
    }
    row[p.scope] = p[field] as number | null;
  }
  // ISO timestamps sort correctly as strings.
  return [...byTs.values()].sort((a, b) =>
    String(a.ts).localeCompare(String(b.ts)),
  );
}

const TRAFFIC_SERIES = [
  { key: "total", label: "Req/min", color: "#3b82f6", dash: false },
  { key: "avg_ms", label: "Śr. czas (ms)", color: "#f97316", dash: false },
  { key: "p95_ms", label: "P95 (ms)", color: "#ef4444", dash: true },
] as const;

const STATUS_SERIES = [
  { key: "ok_2xx", label: "2xx OK", color: "#22c55e" },
  { key: "err_4xx", label: "4xx Błąd klienta", color: "#f59e0b" },
  { key: "err_5xx", label: "5xx Błąd serwera", color: "#ef4444" },
] as const;

function SeriesToggle({
  series,
  hidden,
  onToggle,
}: {
  series: readonly {
    key: string;
    label: string;
    color: string;
    dash?: boolean;
  }[];
  hidden: Set<string>;
  onToggle: (k: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2 mb-1">
      {series.map((s) => {
        const off = hidden.has(s.key);
        return (
          <button
            key={s.key}
            onClick={() => onToggle(s.key)}
            className={clsx(
              "inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium transition-opacity border",
              off
                ? "opacity-30 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500"
                : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700",
            )}
          >
            {s.dash ? (
              <span className="flex gap-0.5">
                <span
                  className="w-2 h-0.5 rounded-full mt-1.5"
                  style={{ background: s.color }}
                />
                <span
                  className="w-1 h-0.5 rounded-full mt-1.5"
                  style={{ background: s.color }}
                />
              </span>
            ) : (
              <span
                className="w-3 h-0.5 rounded-full mt-1"
                style={{ background: s.color }}
              />
            )}
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

function fmtMs(v: number): string {
  if (v >= 10000) return `${(v / 1000).toFixed(0)}k ms`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k ms`;
  return `${v} ms`;
}

// Human-readable duration for cumulative times that can reach hours.
function fmtDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)} min`;
  return `${(ms / 3_600_000).toFixed(1)} h`;
}

// Sequential red heat tint scaled by value/max — for the "Łączny czas" column.
// sqrt spreads the (typically skewed) distribution so mid values are visible.
// Works in both themes: a low-opacity red tint reads fine on light and dark bg.
function heatBg(value: number, max: number): string | undefined {
  if (!max || value <= 0) return undefined;
  const r = Math.sqrt(Math.min(value / max, 1));
  return `rgba(239, 68, 68, ${(0.06 + r * 0.5).toFixed(3)})`;
}

function ChartCard({
  id,
  title,
  expanded,
  onToggleExpand,
  normalHeight,
  fullWidth = false,
  children,
  actions,
  info,
}: {
  id: ChartId;
  title: string;
  expanded: boolean;
  onToggleExpand: (id: ChartId) => void;
  normalHeight: number;
  fullWidth?: boolean;
  children: (height: number) => React.ReactNode;
  actions?: React.ReactNode;
  info?: React.ReactNode;
}) {
  const [showInfo, setShowInfo] = useState(false);
  const height = expanded ? Math.round(normalHeight * 1.9) : normalHeight;

  return (
    <div
      className={clsx(
        "bg-white dark:bg-gray-800 p-4 transition-all",
        (expanded || fullWidth) && "xl:col-span-2",
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {title}
            </p>
            {info && (
              <div className="relative">
                <button
                  onClick={() => setShowInfo((v) => !v)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label="Więcej informacji"
                >
                  <Info size={13} />
                </button>
                {showInfo && (
                  <div
                    className="absolute left-0 top-5 z-30 w-72 bg-gray-900 text-gray-100 text-xs rounded-lg shadow-xl p-3 border border-gray-700"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {info}
                    <button
                      onClick={() => setShowInfo(false)}
                      className="absolute top-1.5 right-2 text-gray-500 hover:text-gray-300 text-xs"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {actions}
        </div>
        <button
          onClick={() => onToggleExpand(id)}
          className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title={expanded ? "Zmniejsz" : "Rozszerz"}
        >
          {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        </button>
      </div>

      {children(height)}
    </div>
  );
}

// One line per ALB scope for a single metric. `data` is already pivoted by
// pivotLbByScope; `scopes` limits which lines to draw (only scopes with data).
function LbScopeLineChart({
  data,
  scopes,
  height,
  fmtTick,
  tickInterval,
  unit,
  yWidth = 40,
}: {
  data: Record<string, number | null | string>[];
  scopes: LoadBalancerScope[];
  height: number;
  fmtTick: (iso: string) => string;
  tickInterval: number;
  unit?: string;
  yWidth?: number;
}) {
  const meta = LB_SCOPE_META.filter((m) => scopes.includes(m.key));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
        <XAxis dataKey="ts" tickFormatter={fmtTick} interval={tickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={yWidth} allowDecimals={false} tickFormatter={unit ? (v) => `${v}${unit}` : undefined} />
        <Tooltip
          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${Math.round(n)}${unit ?? ""}` : "—", name]; }}
          labelFormatter={(l) => fmtTick(l as string)}
          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
          labelStyle={{ color: "#e5e7eb" }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {meta.map((m) => (
          <Line key={m.key} type="monotone" dataKey={m.key} name={m.label} stroke={m.color} strokeWidth={1.5} dot={false} connectNulls />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function MetricsCharts({ enabled }: { enabled: boolean }) {
  const [hours, setHours] = useState(3);
  const [expanded, setExpanded] = useState<ChartId | null>(null);
  const [hiddenTraffic, setHiddenTraffic] = useState<Set<string>>(new Set());
  const [hiddenStatus, setHiddenStatus] = useState<Set<string>>(new Set());
  const [statusMode, setStatusMode] = useState<StatusMode>("abs");
  const [hiddenInfraCharts, setHiddenInfraCharts] = useState<Set<string>>(new Set());
  const [hiddenEcsWeb, setHiddenEcsWeb] = useState<Set<string>>(new Set());
  const [hiddenEcsCelery, setHiddenEcsCelery] = useState<Set<string>>(new Set());
  const [hiddenEcsQgis, setHiddenEcsQgis] = useState<Set<string>>(new Set());
  const [hiddenEcsQgisCache, setHiddenEcsQgisCache] = useState<Set<string>>(new Set());
  const [hiddenEcsCentrifugo, setHiddenEcsCentrifugo] = useState<Set<string>>(new Set());
  const [hiddenEcsCollabora, setHiddenEcsCollabora] = useState<Set<string>>(new Set());
  const [hiddenEcsCeleryBeat, setHiddenEcsCeleryBeat] = useState<Set<string>>(new Set());
  const [hiddenRedis, setHiddenRedis] = useState<Set<string>>(new Set());
  const [hiddenGgReconnect, setHiddenGgReconnect] = useState<Set<string>>(new Set());

  function toggleInfraChart(id: string) {
    setHiddenInfraCharts((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleExpand(id: ChartId) {
    setExpanded((e) => (e === id ? null : id));
  }
  function toggle(setter: React.Dispatch<React.SetStateAction<Set<string>>>) {
    return (key: string) =>
      setter((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      });
  }

  const { data: series = [], isLoading } = useQuery<TimeseriesPoint[]>({
    queryKey: ["dev-metrics-timeseries", hours],
    queryFn: () => devMetricsApi.getTimeseries(hours),
    refetchInterval: 60_000,
    enabled,
  });

  const { data: infraSeries = [], isLoading: infraLoading } = useQuery<
    InfraPoint[]
  >({
    queryKey: ["dev-metrics-infra", hours],
    queryFn: () => devMetricsApi.getInfraTimeseries(hours),
    refetchInterval: 60_000,
    enabled,
  });

  const { data: realtimeSeries = [] } = useQuery<RealtimePoint[]>({
    queryKey: ["dev-metrics-realtime", hours],
    queryFn: () => devMetricsApi.getRealtimeTimeseries(hours),
    refetchInterval: 60_000,
    enabled,
  });

  const { data: lb = { points: [], scopes: [] } } = useQuery({
    queryKey: ["dev-metrics-loadbalancers", hours],
    queryFn: () => devMetricsApi.getLoadBalancerTimeseries(hours),
    refetchInterval: 60_000,
    enabled,
  });
  const lbPoints = lb.points;
  const lbScopes = lb.scopes;
  const lbTgScopes = useMemo(
    () => lbScopes.filter((s) => s !== "alb"),
    [lbScopes],
  );
  const lbRequests = useMemo(() => pivotLbByScope(lbPoints, "request_count"), [lbPoints]);
  const lbP95 = useMemo(() => pivotLbByScope(lbPoints, "rt_p95_ms"), [lbPoints]);
  const lb5xx = useMemo(() => pivotLbByScope(lbPoints, "http_5xx"), [lbPoints]);
  const lb4xx = useMemo(() => pivotLbByScope(lbPoints, "http_4xx"), [lbPoints]);
  const lbUnhealthy = useMemo(() => pivotLbByScope(lbPoints, "unhealthy_hosts"), [lbPoints]);
  const lbAlbSeries = useMemo(
    () => lbPoints.filter((p) => p.scope === "alb"),
    [lbPoints],
  );
  // Running elg5-collabora tasks: the latest snapshot plus the window minimum, so a
  // restart (a dip to 0) stays visible without a separate chart.
  const collaboraTasks = useMemo(() => {
    const counts = infraSeries
      .map((p) => p.ecs_collabora_tasks)
      .filter((v): v is number => v != null);
    return counts.length ? { latest: counts[counts.length - 1], min: Math.min(...counts) } : null;
  }, [infraSeries]);
  const lbTickInterval =
    lbRequests.length > 5000 ? 479 :
    lbRequests.length > 2000 ? 239 :
    lbRequests.length > 500 ? 59 :
    lbRequests.length > 120 ? 29 :
    lbRequests.length > 60 ? 14 :
    lbRequests.length > 30 ? 9 : 4;

  const fmtTickCtx = useMemo(
    () => (iso: string) => {
      const d = new Date(iso);
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      if (hours > 24) return `${d.getDate()}/${d.getMonth() + 1} ${hh}:00`;
      return `${hh}:${mm}`;
    },
    [hours],
  );

  const infraTickInterval =
    infraSeries.length > 5000 ? 479 :
    infraSeries.length > 2000 ? 239 :
    infraSeries.length > 500 ? 59 :
    infraSeries.length > 120 ? 29 :
    infraSeries.length > 60 ? 14 :
    infraSeries.length > 30 ? 9 : 4;

  // Scheduler health series — display keys capped at 2000 ms; raw keys kept for tooltip
  const HEALTH_CAP_MS = 2000;
  const healthSeries = useMemo(
    () =>
      series.map((p) => ({
        ...p,
        drift_ms_display:
          p.drift_ms != null ? Math.min(p.drift_ms, HEALTH_CAP_MS) : null,
        shakehand_ms_display:
          p.shakehand_ms != null
            ? Math.min(p.shakehand_ms, HEALTH_CAP_MS)
            : null,
        qgis_shakehand_ms_display:
          p.qgis_shakehand_ms != null
            ? Math.min(p.qgis_shakehand_ms, HEALTH_CAP_MS)
            : null,
      })),
    [series],
  );

  // Derived series for status chart based on selected mode
  const statusSeries = useMemo(() => {
    if (statusMode === "abs") return series;
    return series.map((p) => {
      const total = p.total || 1;
      const pct = (n: number) => Math.round((n / total) * 1000) / 10; // 1 decimal
      if (statusMode === "pct") {
        return {
          ...p,
          ok_2xx: pct(p.ok_2xx),
          err_4xx: pct(p.err_4xx),
          err_5xx: pct(p.err_5xx),
        };
      }
      // err_pct — only error percentages, hide 2xx
      return {
        ...p,
        ok_2xx: 0,
        err_4xx: pct(p.err_4xx),
        err_5xx: pct(p.err_5xx),
      };
    });
  }, [series, statusMode]);

  const isPct = statusMode !== "abs";
  const tickInterval =
    series.length > 5000 ? 479 :
    series.length > 2000 ? 239 :
    series.length > 500 ? 59 :
    series.length > 120 ? 29 :
    series.length > 60 ? 14 :
    series.length > 30 ? 9 : 4;

  return (
    <section>
      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          Wykresy (per min.)
        </h2>
        <div className="flex gap-1 md:ml-auto">
          {HOURS_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setHours(o.value)}
              className={clsx(
                "px-2.5 py-1 text-xs rounded-md font-medium transition-colors",
                hours === o.value
                  ? "bg-[var(--color-primary)] text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600",
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="h-48 flex items-center justify-center">
          <Spinner />
        </div>
      )}

      {!isLoading && series.length === 0 && (
        <div className="h-48 flex items-center justify-center text-sm text-gray-400">
          Brak danych — wykresy będą dostępne po uruchomieniu zadania Celery{" "}
          <code className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded ml-1">
            collect_request_metrics
          </code>
        </div>
      )}

      {!isLoading && series.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Chart 1: Traffic + Latency */}
          <ChartCard
            id="traffic"
            title="Requesty/min & czas odpowiedzi"
            expanded={expanded === "traffic"}
            onToggleExpand={toggleExpand}
            normalHeight={220}
            actions={
              <SeriesToggle
                series={TRAFFIC_SERIES}
                hidden={hiddenTraffic}
                onToggle={toggle(setHiddenTraffic)}
              />
            }
          >
            {(h) => (
              <ResponsiveContainer width="100%" height={h}>
                <ComposedChart
                  data={series}
                  margin={{ top: 4, right: 48, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="#3b82f6"
                        stopOpacity={0.25}
                      />
                      <stop
                        offset="95%"
                        stopColor="#3b82f6"
                        stopOpacity={0.03}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    strokeOpacity={0.3}
                  />
                  <XAxis
                    dataKey="ts"
                    tickFormatter={fmtTickCtx}
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    interval={tickInterval}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    width={32}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                    tickFormatter={(v) => `${v}ms`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="total"
                    name="Req/min"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#gradTotal)"
                    dot={false}
                    activeDot={{ r: 3 }}
                    hide={hiddenTraffic.has("total")}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="avg_ms"
                    name="Śr. czas (ms)"
                    stroke="#f97316"
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={{ r: 3 }}
                    connectNulls
                    hide={hiddenTraffic.has("avg_ms")}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="p95_ms"
                    name="P95 (ms)"
                    stroke="#ef4444"
                    strokeWidth={1.5}
                    strokeDasharray="4 2"
                    dot={false}
                    activeDot={{ r: 3 }}
                    connectNulls
                    hide={hiddenTraffic.has("p95_ms")}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Chart 2: Status distribution */}
          <ChartCard
            id="status"
            title="Rozkład statusów"
            expanded={expanded === "status"}
            onToggleExpand={toggleExpand}
            normalHeight={220}
            actions={
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                {/* Mode selector */}
                <div className="flex gap-0.5">
                  {STATUS_MODES.map((m) => (
                    <button
                      key={m.value}
                      onClick={() => setStatusMode(m.value)}
                      title={m.title}
                      className={clsx(
                        "px-2 py-0.5 text-xs rounded font-medium transition-colors",
                        statusMode === m.value
                          ? "bg-[var(--color-primary)] text-white"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600",
                      )}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                {/* Series toggles — hidden in err_pct mode (ok_2xx is irrelevant) */}
                {statusMode !== "err_pct" && (
                  <SeriesToggle
                    series={STATUS_SERIES}
                    hidden={hiddenStatus}
                    onToggle={toggle(setHiddenStatus)}
                  />
                )}
              </div>
            }
          >
            {(h) => (
              <ResponsiveContainer width="100%" height={h}>
                <AreaChart
                  data={statusSeries}
                  margin={{ top: 4, right: 12, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient id="grad2xx" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.5} />
                      <stop
                        offset="95%"
                        stopColor="#22c55e"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                    <linearGradient id="grad4xx" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.7} />
                      <stop
                        offset="95%"
                        stopColor="#f59e0b"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                    <linearGradient id="grad5xx" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                      <stop
                        offset="95%"
                        stopColor="#ef4444"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    strokeOpacity={0.3}
                  />
                  <XAxis
                    dataKey="ts"
                    tickFormatter={fmtTickCtx}
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    interval={tickInterval}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    width={isPct ? 40 : 32}
                    tickFormatter={isPct ? (v) => `${v}%` : undefined}
                    domain={statusMode === "pct" ? [0, 100] : undefined}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="ok_2xx"
                    name="2xx OK"
                    stroke="#22c55e"
                    strokeWidth={1.5}
                    fill="url(#grad2xx)"
                    dot={false}
                    hide={
                      statusMode === "err_pct" || hiddenStatus.has("ok_2xx")
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="err_4xx"
                    name={isPct ? "4xx Błąd klienta %" : "4xx Błąd klienta"}
                    stroke="#f59e0b"
                    strokeWidth={1.5}
                    fill="url(#grad4xx)"
                    dot={false}
                    hide={hiddenStatus.has("err_4xx")}
                  />
                  <Area
                    type="monotone"
                    dataKey="err_5xx"
                    name={isPct ? "5xx Błąd serwera %" : "5xx Błąd serwera"}
                    stroke="#ef4444"
                    strokeWidth={1.5}
                    fill="url(#grad5xx)"
                    dot={false}
                    hide={hiddenStatus.has("err_5xx")}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Chart 3: Success rate */}
          <ChartCard
            id="rate"
            title="Success rate (%)"
            expanded={expanded === "rate"}
            onToggleExpand={toggleExpand}
            normalHeight={130}
          >
            {(h) => (
              <ResponsiveContainer width="100%" height={h}>
                <AreaChart
                  data={series}
                  margin={{ top: 4, right: 12, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient id="gradRate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                      <stop
                        offset="95%"
                        stopColor="#a855f7"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    strokeOpacity={0.3}
                  />
                  <XAxis
                    dataKey="ts"
                    tickFormatter={fmtTickCtx}
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    interval={tickInterval}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="success_rate"
                    name="Success rate"
                    stroke="#a855f7"
                    strokeWidth={2}
                    fill="url(#gradRate)"
                    dot={false}
                    connectNulls
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Chart 4: Scheduler health — always full width */}
          <ChartCard
            id="health"
            title="Zdrowie schedulera (ms)"
            expanded={expanded === "health"}
            onToggleExpand={toggleExpand}
            normalHeight={150}
            fullWidth
            info={
              <div className="space-y-2.5 pr-4">
                <div>
                  <p className="font-semibold text-amber-400 mb-0.5">
                    Drift (słupki, żółte)
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Ile milisekund <em>spóźnił się</em> task Celery w stosunku
                    do zaplanowanej pełnej minuty (:00). Normalnie: poniżej 500
                    ms. Duże wartości wskazują na przeciążonego workera lub
                    schedulera.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-cyan-400 mb-0.5">
                    Shakehand (linia, niebieska)
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Czas odpowiedzi wewnętrznego HTTP ping — worker Celery
                    odpytuje backend Django i mierzy czas pełnego round-tripu.
                    Pokazuje <em>jak szybko backend odpowiada na żądania</em>.
                    Normalnie: poniżej 100 ms. Skoki mogą oznaczać przeciążenie
                    serwera lub problemy z siecią.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-purple-400 mb-0.5">
                    QGIS shakehand (linia, fioletowa)
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Czas WMS GetCapabilities → QGIS Server (przez cache nginx).
                    Mierzy zdrowie ścieżki <em>backend → qgis-cache → QGIS</em>.
                    Normalnie: 50–500 ms. Brak danych = QGIS niedostępny.
                  </p>
                </div>
              </div>
            }
          >
            {(h) => (
              <ResponsiveContainer width="100%" height={h}>
                <ComposedChart
                  data={healthSeries}
                  margin={{ top: 4, right: 12, bottom: 0, left: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    strokeOpacity={0.3}
                  />
                  <XAxis
                    dataKey="ts"
                    tickFormatter={fmtTickCtx}
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    interval={tickInterval}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    width={64}
                    tickFormatter={fmtMs}
                    domain={[0, HEALTH_CAP_MS]}
                  />
                  <Tooltip content={<HealthTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                    formatter={(value) => (
                      <span style={{ color: "#9ca3af" }}>{value}</span>
                    )}
                  />
                  <Bar
                    dataKey="drift_ms_display"
                    name="Drift (ms)"
                    fill="#f59e0b"
                    opacity={0.7}
                    radius={[2, 2, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="shakehand_ms_display"
                    name="Shakehand (ms)"
                    stroke="#06b6d4"
                    strokeWidth={1.5}
                    dot={false}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="qgis_shakehand_ms_display"
                    name="QGIS shakehand (ms)"
                    stroke="#a855f7"
                    strokeWidth={1.5}
                    strokeDasharray="4 2"
                    dot={false}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      )}

      {/* ── Infra charts (ECS + RDS) ── */}
      {(infraLoading || infraSeries.length > 0) && (
        <>
          <div className="flex items-center gap-3 mt-6 mb-4 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              Infrastruktura (per minuta)
            </h2>
            <div className="flex flex-wrap gap-1 ml-auto">
              {INFRA_PANELS.map((p) => {
                const hidden = hiddenInfraCharts.has(p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => toggleInfraChart(p.id)}
                    className={clsx(
                      "px-2.5 py-0.5 text-xs rounded font-medium transition-all border",
                      hidden
                        ? "opacity-40 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 bg-transparent"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600",
                    )}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {infraLoading && infraSeries.length === 0 && (
            <div className="h-48 flex items-center justify-center">
              <Spinner />
            </div>
          )}

          {infraSeries.length > 0 && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

              {/* ECS Web */}
              {!hiddenInfraCharts.has("ecs-web") && (
                <ChartCard
                  id="ecs-web"
                  title="ECS Web — CPU & RAM (%)"
                  expanded={expanded === "ecs-web"}
                  onToggleExpand={toggleExpand}
                  normalHeight={180}
                  actions={
                    <SeriesToggle
                      series={ECS_WEB_SERIES}
                      hidden={hiddenEcsWeb}
                      onToggle={toggle(setHiddenEcsWeb)}
                    />
                  }
                  info="CPU i pamięć kontenera elg5-web (Fargate). CloudWatch Container Insights, opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={infraSeries} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={38} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(1)}%` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Line type="monotone" dataKey="ecs_web_cpu" name="CPU %" stroke="#3b82f6" strokeWidth={1.5} dot={false} connectNulls hide={hiddenEcsWeb.has("ecs_web_cpu")} />
                        <Line type="monotone" dataKey="ecs_web_mem" name="RAM %" stroke="#6366f1" strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 2" hide={hiddenEcsWeb.has("ecs_web_mem")} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* ECS Celery */}
              {!hiddenInfraCharts.has("ecs-celery") && (
                <ChartCard
                  id="ecs-celery"
                  title="ECS Celery — CPU & RAM (%)"
                  expanded={expanded === "ecs-celery"}
                  onToggleExpand={toggleExpand}
                  normalHeight={180}
                  actions={
                    <SeriesToggle
                      series={ECS_CELERY_SERIES}
                      hidden={hiddenEcsCelery}
                      onToggle={toggle(setHiddenEcsCelery)}
                    />
                  }
                  info="CPU i pamięć kontenera elg5-celery (Fargate). CloudWatch Container Insights, opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={infraSeries} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={38} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(1)}%` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Line type="monotone" dataKey="ecs_celery_cpu" name="CPU %" stroke="#f59e0b" strokeWidth={1.5} dot={false} connectNulls hide={hiddenEcsCelery.has("ecs_celery_cpu")} />
                        <Line type="monotone" dataKey="ecs_celery_mem" name="RAM %" stroke="#10b981" strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 2" hide={hiddenEcsCelery.has("ecs_celery_mem")} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* ECS QGIS Server */}
              {!hiddenInfraCharts.has("ecs-qgis") && (
                <ChartCard
                  id="ecs-qgis"
                  title="QGIS Server — CPU & RAM (%)"
                  expanded={expanded === "ecs-qgis"}
                  onToggleExpand={toggleExpand}
                  normalHeight={180}
                  actions={
                    <SeriesToggle
                      series={ECS_QGIS_SERIES}
                      hidden={hiddenEcsQgis}
                      onToggle={toggle(setHiddenEcsQgis)}
                    />
                  }
                  info="CPU i pamięć kontenera elg5-qgis (Fargate, 1024 CPU / 4096 MB). CloudWatch Container Insights, opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={infraSeries} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={38} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(1)}%` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Line type="monotone" dataKey="ecs_qgis_cpu" name="CPU %" stroke="#8b5cf6" strokeWidth={1.5} dot={false} connectNulls hide={hiddenEcsQgis.has("ecs_qgis_cpu")} />
                        <Line type="monotone" dataKey="ecs_qgis_mem" name="RAM %" stroke="#ec4899" strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 2" hide={hiddenEcsQgis.has("ecs_qgis_mem")} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* ECS QGIS Cache (nginx tile cache) */}
              {!hiddenInfraCharts.has("ecs-qgis-cache") && (
                <ChartCard
                  id="ecs-qgis-cache"
                  title="QGIS Cache — CPU & RAM (%)"
                  expanded={expanded === "ecs-qgis-cache"}
                  onToggleExpand={toggleExpand}
                  normalHeight={180}
                  actions={
                    <SeriesToggle
                      series={ECS_QGIS_CACHE_SERIES}
                      hidden={hiddenEcsQgisCache}
                      onToggle={toggle(setHiddenEcsQgisCache)}
                    />
                  }
                  info="CPU i pamięć kontenera elg5-qgis-cache (nginx tile cache, Fargate, 256 CPU / 512 MB). CloudWatch Container Insights, opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={infraSeries} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={38} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(1)}%` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Line type="monotone" dataKey="ecs_qgis_cache_cpu" name="CPU %" stroke="#14b8a6" strokeWidth={1.5} dot={false} connectNulls hide={hiddenEcsQgisCache.has("ecs_qgis_cache_cpu")} />
                        <Line type="monotone" dataKey="ecs_qgis_cache_mem" name="RAM %" stroke="#0ea5e9" strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 2" hide={hiddenEcsQgisCache.has("ecs_qgis_cache_mem")} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* ECS Edge — Centrifugo (GG realtime server) + nginx SPA in one Fargate task */}
              {!hiddenInfraCharts.has("ecs-centrifugo") && (
                <ChartCard
                  id="ecs-centrifugo"
                  title="Edge: Centrifugo (GG) + nginx — CPU & RAM (%)"
                  expanded={expanded === "ecs-centrifugo"}
                  onToggleExpand={toggleExpand}
                  normalHeight={180}
                  actions={
                    <SeriesToggle
                      series={ECS_CENTRIFUGO_SERIES}
                      hidden={hiddenEcsCentrifugo}
                      onToggle={toggle(setHiddenEcsCentrifugo)}
                    />
                  }
                  info="Serwer real-time GG (Centrifugo) — od 2026-10-02 w zadaniu elg5-edge razem z nginx-em SPA (Fargate 256 CPU / 512 MB); metryka obejmuje całe zadanie, ale nginx to kilka MB i ~0% CPU. To on obsługuje czat — nie ECS Web. Opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={infraSeries} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={38} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(1)}%` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Line type="monotone" dataKey="ecs_centrifugo_cpu" name="CPU %" stroke="#f43f5e" strokeWidth={1.5} dot={false} connectNulls hide={hiddenEcsCentrifugo.has("ecs_centrifugo_cpu")} />
                        <Line type="monotone" dataKey="ecs_centrifugo_mem" name="RAM %" stroke="#fb923c" strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 2" hide={hiddenEcsCentrifugo.has("ecs_centrifugo_mem")} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* ECS Collabora (edytor pism w przeglądarce) */}
              {!hiddenInfraCharts.has("ecs-collabora") && (
                <ChartCard
                  id="ecs-collabora"
                  title="Collabora (edytor) — CPU & RAM (%)"
                  expanded={expanded === "ecs-collabora"}
                  onToggleExpand={toggleExpand}
                  normalHeight={180}
                  actions={
                    <div className="flex flex-wrap items-center gap-x-3">
                      <SeriesToggle
                        series={ECS_COLLABORA_SERIES}
                        hidden={hiddenEcsCollabora}
                        onToggle={toggle(setHiddenEcsCollabora)}
                      />
                      {collaboraTasks && (
                        <span
                          className={clsx(
                            "mt-2 mb-1 text-xs",
                            collaboraTasks.latest === 0 ? "text-red-600 dark:text-red-400" : "text-gray-600 dark:text-gray-300",
                          )}
                          title="Uruchomione zadania ECS elg5-collabora (DescribeServices, próbka co minutę)"
                        >
                          Zadania ECS: <b>{collaboraTasks.latest}</b>
                          {collaboraTasks.min < collaboraTasks.latest && (
                            <span className="text-amber-600 dark:text-amber-400"> · min {collaboraTasks.min} w oknie</span>
                          )}
                        </span>
                      )}
                    </div>
                  }
                  info="Edytor pism ODT/DOCX/XLSX w przeglądarce (elg5-collabora, Fargate 0,25 vCPU / 2 GB od 2026-10-02, office.elgw5.com). Każdy otwarty dokument to osobny proces kit (~100–300 MB) — RAM rośnie z liczbą otwartych plików; bezczynny coolwsd z zapasowymi kitami zajmuje już ~1 GB, więc ~50% RAM w spoczynku to norma, nie wyciek. Przy 0,25 vCPU otwarcie dokumentu i konwersja trwają dłużej niż kiedyś — jeśli CPU stale siedzi wysoko, pierwsza dźwignia to 0,5 vCPU (terraform/prod/collabora.tf). Pusty wykres = usługa nie raportuje (nie wstała) — patrz karta „Collabora” niżej."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={infraSeries} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={38} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(1)}%` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Line type="monotone" dataKey="ecs_collabora_cpu" name="CPU %" stroke="#0ea5e9" strokeWidth={1.5} dot={false} connectNulls hide={hiddenEcsCollabora.has("ecs_collabora_cpu")} />
                        <Line type="monotone" dataKey="ecs_collabora_mem" name="RAM %" stroke="#a3e635" strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 2" hide={hiddenEcsCollabora.has("ecs_collabora_mem")} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* ECS Celery beat (harmonogram zadań cyklicznych) */}
              {!hiddenInfraCharts.has("ecs-celery-beat") && (
                <ChartCard
                  id="ecs-celery-beat"
                  title="Celery beat — CPU & RAM (%)"
                  expanded={expanded === "ecs-celery-beat"}
                  onToggleExpand={toggleExpand}
                  normalHeight={180}
                  actions={
                    <SeriesToggle
                      series={ECS_CELERY_BEAT_SERIES}
                      hidden={hiddenEcsCeleryBeat}
                      onToggle={toggle(setHiddenEcsCeleryBeat)}
                    />
                  }
                  info="Harmonogram zadań cyklicznych Celery (elg5-celery-beat, Fargate 0,25 vCPU / 0,5 GB). Zawsze dokładnie 1 sztuka — dwie oznaczałyby podwójne zadania. Sam nic nie wykonuje, tylko wrzuca zadania do kolejki, więc CPU bliskie zeru to norma; RAM to załadowane Django (~250 MB). Karta dodana 2026-10-12, żeby na produkcyjnych danych ocenić, czy beat zmieści się w zadaniu edge obok nginx i Centrifugo. Pusty wykres = usługa nie raportuje. Opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={infraSeries} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={38} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(1)}%` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Line type="monotone" dataKey="ecs_celery_beat_cpu" name="CPU %" stroke="#f59e0b" strokeWidth={1.5} dot={false} connectNulls hide={hiddenEcsCeleryBeat.has("ecs_celery_beat_cpu")} />
                        <Line type="monotone" dataKey="ecs_celery_beat_mem" name="RAM %" stroke="#c084fc" strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 2" hide={hiddenEcsCeleryBeat.has("ecs_celery_beat_mem")} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* ElastiCache Redis CPU (Engine + Node) */}
              {!hiddenInfraCharts.has("redis-engine") && (
                <ChartCard
                  id="redis-engine"
                  title="Redis — CPU (%)"
                  expanded={expanded === "redis-engine"}
                  onToggleExpand={toggleExpand}
                  normalHeight={180}
                  actions={
                    <SeriesToggle
                      series={REDIS_SERIES}
                      hidden={hiddenRedis}
                      onToggle={toggle(setHiddenRedis)}
                    />
                  }
                  info="ElastiCache elg5-redis — WSPÓLNY dla brokera Celery (db0), cache Django (db2) i silnika Centrifugo (db2). Engine CPU = jednowątkowy proces komend Redis; jego nasycenie tnie GG nawet gdy reszta jest zdrowa. Opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={infraSeries} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={38} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(1)}%` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Line type="monotone" dataKey="redis_engine_cpu" name="Engine CPU %" stroke="#ef4444" strokeWidth={1.5} dot={false} connectNulls hide={hiddenRedis.has("redis_engine_cpu")} />
                        <Line type="monotone" dataKey="redis_cpu" name="Node CPU %" stroke="#f59e0b" strokeWidth={1.5} dot={false} connectNulls strokeDasharray="4 2" hide={hiddenRedis.has("redis_cpu")} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* Redis connections */}
              {!hiddenInfraCharts.has("redis-conn") && (
                <ChartCard
                  id="redis-conn"
                  title="Redis — Połączenia"
                  expanded={expanded === "redis-conn"}
                  onToggleExpand={toggleExpand}
                  normalHeight={160}
                  info="CurrConnections ElastiCache. Nagły wzrost = burst klientów/reconnectów albo pooler pod korek. Opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={infraSeries} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={38} allowDecimals={false} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? String(Math.round(n)) : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Bar dataKey="redis_connections" name="Połączenia" fill="#8b5cf6" opacity={0.7} radius={[2, 2, 0, 0]} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* ALB — Centrifugo target group response time */}
              {!hiddenInfraCharts.has("alb-centrifugo-rt") && (
                <ChartCard
                  id="alb-centrifugo-rt"
                  title="ALB GG — czas odpowiedzi (ms)"
                  expanded={expanded === "alb-centrifugo-rt"}
                  onToggleExpand={toggleExpand}
                  normalHeight={160}
                  info="TargetResponseTime na target group Centrifugo (ścieżka /connection/*). Skoki = wolny upgrade WS / handshake. Wymaga wstrzyknięcia wymiarów ALB przez terraform; puste = brak konfiguracji. Opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <AreaChart data={infraSeries} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                        <defs>
                          <linearGradient id="gradAlbGg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `${v}ms`} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(1)} ms` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Area type="monotone" dataKey="alb_centrifugo_rt_ms" name="Czas odpowiedzi" stroke="#06b6d4" strokeWidth={1.5} fill="url(#gradAlbGg)" dot={false} connectNulls />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* GG reconnecty — z RtcDiagnosticEvent (client-side) */}
              {!hiddenInfraCharts.has("gg-reconnect") && (
                <ChartCard
                  id="gg-reconnect"
                  title="GG — reconnecty / rozłączenia (per min)"
                  expanded={expanded === "gg-reconnect"}
                  onToggleExpand={toggleExpand}
                  normalHeight={180}
                  actions={
                    <SeriesToggle
                      series={GG_RECONNECT_SERIES}
                      hidden={hiddenGgReconnect}
                      onToggle={toggle(setHiddenGgReconnect)}
                    />
                  }
                  info="Zdarzenia transportu Centrifugo z przeglądarek (RtcDiagnosticEvent): próby połączenia, rozłączenia, błędy, ponowne subskrypcje. Reconnect-storm nie widać na CPU/RAM — TU widać. Zdrowo = blisko zera."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={realtimeSeries} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} minTickGap={24} />
                        <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={32} allowDecimals={false} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? String(Math.round(n)) : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        {GG_RECONNECT_SERIES.map((s) => (
                          <Bar key={s.key} dataKey={s.key} name={s.label} stackId="gg" fill={s.color} opacity={0.85} hide={hiddenGgReconnect.has(s.key)} />
                        ))}
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* RDS CPU */}
              {!hiddenInfraCharts.has("rds-cpu") && (
                <ChartCard
                  id="rds-cpu"
                  title="PostgreSQL — CPU (%)"
                  expanded={expanded === "rds-cpu"}
                  onToggleExpand={toggleExpand}
                  normalHeight={160}
                  info="CPU instancji RDS PostgreSQL (CPUUtilization). Opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <AreaChart data={infraSeries} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                        <defs>
                          <linearGradient id="gradRdsCpu" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={38} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(1)}%` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Area type="monotone" dataKey="rds_cpu" name="CPU %" stroke="#ef4444" strokeWidth={1.5} fill="url(#gradRdsCpu)" dot={false} connectNulls />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* RDS Memory */}
              {!hiddenInfraCharts.has("rds-mem") && (
                <ChartCard
                  id="rds-mem"
                  title="PostgreSQL — RAM dostępny (MB)"
                  expanded={expanded === "rds-mem"}
                  onToggleExpand={toggleExpand}
                  normalHeight={160}
                  info="Wolna pamięć RAM instancji RDS (FreeableMemory). Większa wartość = lepiej. Opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <AreaChart data={infraSeries} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                        <defs>
                          <linearGradient id="gradRdsMem" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `${Math.round(v)} MB`} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${Math.round(n)} MB` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Area type="monotone" dataKey="rds_mem_free_mb" name="RAM dostępny" stroke="#10b981" strokeWidth={1.5} fill="url(#gradRdsMem)" dot={false} connectNulls />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* RDS Disk */}
              {!hiddenInfraCharts.has("rds-disk") && (
                <ChartCard
                  id="rds-disk"
                  title="PostgreSQL — Dysk wolny (GB)"
                  expanded={expanded === "rds-disk"}
                  onToggleExpand={toggleExpand}
                  normalHeight={160}
                  info="Wolna przestrzeń dyskowa instancji RDS (FreeStorageSpace). Większa wartość = lepiej. Opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <AreaChart data={infraSeries} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                        <defs>
                          <linearGradient id="gradRdsDisk" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={52} tickFormatter={(v) => `${Number(v).toFixed(1)} GB`} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? `${n.toFixed(2)} GB` : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Area type="monotone" dataKey="rds_storage_free_gb" name="Dysk wolny" stroke="#f59e0b" strokeWidth={1.5} fill="url(#gradRdsDisk)" dot={false} connectNulls />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

              {/* RDS Connections */}
              {!hiddenInfraCharts.has("rds-conn") && (
                <ChartCard
                  id="rds-conn"
                  title="PostgreSQL — Połączenia"
                  expanded={expanded === "rds-conn"}
                  onToggleExpand={toggleExpand}
                  normalHeight={160}
                  info="Liczba aktywnych połączeń z bazą PostgreSQL (DatabaseConnections). Opóźnienie ~2 min."
                >
                  {(h) => (
                    <ResponsiveContainer width="100%" height={h}>
                      <ComposedChart data={infraSeries} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                        <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={infraTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={32} allowDecimals={false} />
                        <Tooltip
                          formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? String(Math.round(n)) : "—", name]; }}
                          labelFormatter={(l) => fmtTickCtx(l as string)}
                          contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: "#e5e7eb" }}
                        />
                        <Bar dataKey="rds_connections" name="Połączenia" fill="#6366f1" opacity={0.7} radius={[2, 2, 0, 0]} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              )}

            </div>
          )}
        </>
      )}

      {/* ── Load balancer (ALB): każda ścieżka ruchu osobno ── */}
      {lbPoints.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-[var(--text-secondary)] mb-2">
            Load Balancer (ALB) — ścieżki ruchu
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ChartCard
              id="lb-requests"
              title="ALB — ruch (req/min)"
              expanded={expanded === "lb-requests"}
              onToggleExpand={toggleExpand}
              normalHeight={200}
              info="RequestCount na sam load balancer (całość) oraz każdą target group osobno. Widać, która ścieżka (API / front / kafle QGIS / GG) generuje ruch. Opóźnienie ~2 min."
            >
              {(h) => (
                <LbScopeLineChart data={lbRequests} scopes={lbScopes} height={h} fmtTick={fmtTickCtx} tickInterval={lbTickInterval} yWidth={44} />
              )}
            </ChartCard>

            <ChartCard
              id="lb-p95"
              title="ALB — czas odpowiedzi p95 (ms)"
              expanded={expanded === "lb-p95"}
              onToggleExpand={toggleExpand}
              normalHeight={200}
              info="TargetResponseTime p95 dla całego LB i każdej target group. Skok tylko na jednej ścieżce = problem konkretnej usługi, nie całej infry. Opóźnienie ~2 min."
            >
              {(h) => (
                <LbScopeLineChart data={lbP95} scopes={lbScopes} height={h} fmtTick={fmtTickCtx} tickInterval={lbTickInterval} unit="ms" yWidth={52} />
              )}
            </ChartCard>

            <ChartCard
              id="lb-5xx"
              title="ALB — błędy 5xx (per min)"
              expanded={expanded === "lb-5xx"}
              onToggleExpand={toggleExpand}
              normalHeight={200}
              info="HTTPCode_Target_5XX_Count per ścieżka — 5xx zwrócone przez usługę za target group. Zdrowo = zero. Osobno 5xx generowane przez sam LB (502/503) są w metryce elb_5xx na scope „ALB (całość)”. Opóźnienie ~2 min."
            >
              {(h) => (
                <LbScopeLineChart data={lb5xx} scopes={lbScopes} height={h} fmtTick={fmtTickCtx} tickInterval={lbTickInterval} yWidth={40} />
              )}
            </ChartCard>

            <ChartCard
              id="lb-4xx"
              title="ALB — błędy 4xx (per min)"
              expanded={expanded === "lb-4xx"}
              onToggleExpand={toggleExpand}
              normalHeight={200}
              info="HTTPCode_Target_4XX_Count per ścieżka — błędy klienta (401/403/404 itp.). Opóźnienie ~2 min."
            >
              {(h) => (
                <LbScopeLineChart data={lb4xx} scopes={lbScopes} height={h} fmtTick={fmtTickCtx} tickInterval={lbTickInterval} yWidth={40} />
              )}
            </ChartCard>

            <ChartCard
              id="lb-unhealthy"
              title="ALB — niezdrowe hosty (per target group)"
              expanded={expanded === "lb-unhealthy"}
              onToggleExpand={toggleExpand}
              normalHeight={180}
              info="UnHealthyHostCount na każdą target group — ile zadań ECS nie przechodzi health-checku i jest wyłączonych z ruchu. Zdrowo = zero. Opóźnienie ~2 min."
            >
              {(h) => (
                <LbScopeLineChart data={lbUnhealthy} scopes={lbTgScopes} height={h} fmtTick={fmtTickCtx} tickInterval={lbTickInterval} yWidth={32} />
              )}
            </ChartCard>

            <ChartCard
              id="lb-conns"
              title="ALB — połączenia (per min)"
              expanded={expanded === "lb-conns"}
              onToggleExpand={toggleExpand}
              normalHeight={180}
              info="Połączenia na całym load balancerze: aktywne (ActiveConnectionCount), nowe (NewConnectionCount) i odrzucone (RejectedConnectionCount — sygnał wysycenia LB). Opóźnienie ~2 min."
            >
              {(h) => (
                <ResponsiveContainer width="100%" height={h}>
                  <ComposedChart data={lbAlbSeries} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                    <XAxis dataKey="ts" tickFormatter={fmtTickCtx} interval={lbTickInterval} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
                    <Tooltip
                      formatter={(v, name) => { const n = v != null ? Number(v) : null; return [n != null ? String(Math.round(n)) : "—", name]; }}
                      labelFormatter={(l) => fmtTickCtx(l as string)}
                      contentStyle={{ backgroundColor: "#1f2937", border: "1px solid #374151", borderRadius: 6, fontSize: 11 }}
                      labelStyle={{ color: "#e5e7eb" }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="active_conns" name="Aktywne" stroke="#3b82f6" strokeWidth={1.5} dot={false} connectNulls />
                    <Line type="monotone" dataKey="new_conns" name="Nowe" stroke="#22c55e" strokeWidth={1.5} dot={false} connectNulls />
                    <Line type="monotone" dataKey="rejected_conns" name="Odrzucone" stroke="#ef4444" strokeWidth={1.5} dot={false} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        </div>
      )}

      {/* ── Celery: czasy tasków async (p95, kolejka, per typ/user) ── */}
      <CeleryTimingSection hours={hours} enabled={enabled} />
    </section>
  );
}

const METHOD_COLORS: Record<string, string> = {
  GET: "text-[var(--color-primary)] dark:text-blue-400",
  POST: "text-green-600 dark:text-green-400",
  PATCH: "text-amber-600 dark:text-amber-400",
  PUT: "text-amber-600 dark:text-amber-400",
  DELETE: "text-red-600 dark:text-red-400",
};

/** Karta „Collabora (edytor pism)": co widzi aplikacja (sonda, liczniki WOPI) i co
 *  widzi AWS (usługa ECS, cel ALB, powód zatrzymania, log) — dla deweloperów bez
 *  konsoli AWS. Odświeża się co minutę. */
function CollaboraStatusSection({ enabled }: { enabled: boolean }) {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<CollaboraStatus>({
    queryKey: ["dev-metrics-collabora"],
    queryFn: devMetricsApi.getCollaboraStatus,
    enabled,
    refetchInterval: 60_000,
  });
  if (!enabled) return null;
  const probe = data?.probe;
  const aws = data?.aws;
  const svc = aws?.service && !("error" in aws.service && Object.keys(aws.service).length === 1) ? aws.service : null;
  const svcError = aws?.service && "error" in aws.service ? aws.service.error : null;
  const targets = Array.isArray(aws?.target_health) ? aws!.target_health : null;
  const targetsError = aws?.target_health && !Array.isArray(aws.target_health) ? aws.target_health.error : null;
  const logTail = Array.isArray(aws?.log_tail) ? aws!.log_tail : null;
  const logError = aws?.log_tail && !Array.isArray(aws.log_tail) ? aws.log_tail.error : null;
  const stopped = aws?.last_stopped_task ?? null;
  const overall = !data ? null : !data.configured ? "off" : probe?.ok ? "ok" : "down";
  return (
    <section>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          Collabora (edytor pism)
        </h2>
        {overall && (
          <span className={clsx(
            "px-2 py-0.5 rounded text-xs font-semibold",
            overall === "ok" && "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
            overall === "down" && "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
            overall === "off" && "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
          )}>
            {overall === "ok" ? "odpowiada" : overall === "down" ? "NIE ODPOWIADA" : "wyłączony (brak konfiguracji)"}
          </span>
        )}
        <button
          type="button"
          onClick={() => refetch()}
          className="ml-auto flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 hover:underline"
          title="Świeża sonda discovery + dane z AWS"
        >
          <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} /> Sprawdź teraz
        </button>
      </div>
      {isLoading && <div className="text-sm text-gray-500">Sprawdzam…</div>}
      {isError && <div className="text-sm text-red-600">Nie udało się pobrać stanu edytora.</div>}
      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-4">
            <SummaryCard
              label="Discovery"
              value={probe?.ok ? "200" : probe?.status != null ? String(probe.status) : "brak"}
              sub={probe?.latency_ms != null ? `${probe.latency_ms} ms` : undefined}
              danger={!probe?.ok}
              info={`Świeży GET ${probe?.url || "(brak adresu)"} z backendu. Skonfigurowany adres, który nie odpowiada, chowa przyciski edycji w aplikacji.`}
            />
            <SummaryCard label="Sesje (24h)" value={data.wopi.sessions} info="Otwarcia edytora (POST /office-editor/sessions/)." />
            <SummaryCard label="Zapisy (24h)" value={data.wopi.saves} info="Zapisy jawne = nowe wersje dokumentów (PutFile bez flagi autozapisu)." />
            <SummaryCard label="Odrzucone autozapisy (24h)" value={data.wopi.discarded} info="Autozapis i zapis przy zamykaniu ramki — odrzucane; wersję tworzy tylko zapis z paska edytora." />
            <SummaryCard label="Awarie zapisu (24h)" value={data.wopi.failures} danger={data.wopi.failures > 0} info="PutFile zakończony błędem (409/413/500 — 413 = dokument ponad limit zapisu) albo sesja odrzucona, bo Collabora nie odpowiada." />
            <SummaryCard label="Formaty" value={`${data.extensions.edit} / ${data.extensions.view}`} sub="edycja / podgląd" info="Rozszerzenia z discovery Collabory (albo lista zapasowa, gdy discovery nie odpowiada)." />
          </div>
          {probe && !probe.ok && (
            <div className="text-sm text-red-700 dark:text-red-400">
              ⚠ Sonda: {probe.error || "brak odpowiedzi"} · {probe.checked_at ? fmtTime(probe.checked_at) : ""}
            </div>
          )}
          {data.wopi.last_failure && (
            <div className="text-xs text-gray-600 dark:text-gray-300">
              Ostatnia awaria zapisu {fmtTime(data.wopi.last_failure.at)} · <span className="font-mono">{data.wopi.last_failure.file_id}</span>:{" "}
              <span className="text-red-700 dark:text-red-400">{data.wopi.last_failure.message}</span>
            </div>
          )}
          <div className="text-xs text-gray-500 font-mono">
            public: {data.public_url || "—"} · internal: {data.internal_url || "—"} · WOPI host: {data.wopi_host_url || "—"}
          </div>

          {aws === null ? (
            <div className="text-xs text-gray-500">Warstwa AWS (ECS / ALB / log) tylko na produkcji (INFRA_METRICS_ENABLED).</div>
          ) : aws && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-1.5">
                <div className="font-semibold text-gray-700 dark:text-gray-200">Usługa ECS elg5-collabora</div>
                {svcError && <div className="text-red-600">{svcError}</div>}
                {svc && (
                  <>
                    <div>
                      Stan: <b>{svc.status}</b> · zadania: uruchomione <b>{svc.running}</b> / oczekujące {svc.pending} / oczekiwane {svc.desired}
                      {svc.task_definition && <> · <span className="font-mono">{svc.task_definition}</span></>}
                    </div>
                    {stopped && (
                      <div className="text-red-700 dark:text-red-400">
                        Ostatnie zatrzymane zadanie {stopped.stopped_at ? fmtTime(stopped.stopped_at) : ""}: {stopped.stopped_reason}
                        {stopped.exit_code != null && <> · exit {stopped.exit_code}</>}
                        {stopped.container_reason && <> · {stopped.container_reason}</>}
                      </div>
                    )}
                    {svc.events && svc.events.length > 0 && (
                      <ul className="space-y-0.5 text-gray-600 dark:text-gray-300">
                        {svc.events.map((e, i) => (
                          <li key={i}><span className="font-mono">{fmtTime(e.at)}</span> {e.message}</li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
                <div className="font-semibold text-gray-700 dark:text-gray-200 pt-1">Cel ALB (elg5-collabora-tg)</div>
                {targetsError && <div className="text-red-600">{targetsError}</div>}
                {targets && targets.length === 0 && <div className="text-red-600">Brak zarejestrowanych celów — zadanie nie wstało.</div>}
                {targets && targets.map((t) => (
                  <div key={t.target}>
                    <span className="font-mono">{t.target}</span>: <b className={t.state === "healthy" ? "text-green-700" : "text-red-700"}>{t.state}</b>
                    {t.reason && <> · {t.reason}</>}{t.description && <> — {t.description}</>}
                  </div>
                ))}
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-1">
                <div className="font-semibold text-gray-700 dark:text-gray-200">Log /ecs/elg5/collabora (błędy z 6 h)</div>
                {logError && <div className="text-red-600">{logError}</div>}
                {logTail && logTail.length === 0 && <div className="text-gray-500">Brak linii ERR/WRN/FATAL (poza znanym szumem) w ostatnich 6 h.</div>}
                {logTail && logTail.length > 0 && (
                  <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-snug text-gray-700 dark:text-gray-300">
                    {logTail.map((e) => `${fmtTime(e.at)} ${e.message}`).join("\n")}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** Karta „Błędy serwera (log web)": 5xx z access logu gunicorna i linie błędów
 *  z /ecs/elg5/web. Silk widzi request dopiero z wnętrza Django, więc 500 sprzed
 *  zapisu i zabity worker (ALB oddaje wtedy 502 sam) są tylko tutaj. Ładuje się na
 *  żądanie — przeszukanie doby logu trwa kilka sekund. */
function WebErrorLogSection({ enabled }: { enabled: boolean }) {
  const [hours, setHours] = useState(24);
  const [open, setOpen] = useState(false);
  const { data, isFetching, isError, refetch } = useQuery<WebErrorLog>({
    queryKey: ["dev-metrics-web-errors", hours],
    queryFn: () => devMetricsApi.getWebErrors(hours),
    enabled: enabled && open,
    staleTime: 5 * 60_000,
  });
  if (!enabled) return null;
  return (
    <section>
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          Błędy serwera (log web)
        </h2>
        <select
          value={hours}
          onChange={(e) => setHours(Number(e.target.value))}
          className="text-xs border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800"
        >
          {[6, 24, 72, 168].map((h) => (
            <option key={h} value={h}>{h < 48 ? `${h} h` : `${h / 24} dni`}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => (open ? refetch() : setOpen(true))}
          className="ml-auto flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 hover:underline"
          title="Przeszukaj log /ecs/elg5/web w CloudWatch"
        >
          <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} /> {open ? "Odśwież" : "Pokaż"}
        </button>
      </div>
      {!open && (
        <div className="text-xs text-gray-500">
          5xx z access logu gunicorna i błędy (Django 500, WORKER TIMEOUT, [ERROR]) — to, czego statystyki Silka nie widzą.
        </div>
      )}
      {open && isFetching && !data && <div className="text-sm text-gray-500">Przeszukuję log…</div>}
      {isError && <div className="text-sm text-red-600">Nie udało się przeszukać logu.</div>}
      {open && data === null && (
        <div className="text-xs text-gray-500">Log web jest dostępny tylko na produkcji (INFRA_METRICS_ENABLED).</div>
      )}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs">
          <WebLogBox title={`Odpowiedzi 5xx (${data.hours} h)`} section={data.responses_5xx} />
          <WebLogBox title={`Linie błędów (${data.hours} h)`} section={data.errors} />
        </div>
      )}
    </section>
  );
}

function WebLogBox({ title, section }: { title: string; section: WebLogSection | null }) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-1.5 min-w-0">
      <div className="font-semibold text-gray-700 dark:text-gray-200">
        {title}
        {section && "total" in section && (
          <span className="ml-2 font-normal text-gray-500">
            {section.total}
            {section.truncated ? "+ (przeszukiwanie ucięte)" : ""} · pokazuję najnowsze {section.rows.length}
          </span>
        )}
      </div>
      {section && "error" in section && <div className="text-red-600">{section.error}</div>}
      {section && "rows" in section && section.rows.length === 0 && <div className="text-gray-500">Brak.</div>}
      {section && "rows" in section && section.rows.length > 0 && (
        <ul className="max-h-80 overflow-auto space-y-1">
          {section.rows.map((row, i) => (
            <li key={i} className="font-mono text-[11px] leading-snug text-gray-700 dark:text-gray-300">
              {row.context && row.context.length > 0 ? (
                <details>
                  <summary className="cursor-pointer whitespace-pre-wrap break-all">
                    {fmtTime(row.at)} {row.message}
                  </summary>
                  <pre className="mt-1 ml-3 whitespace-pre-wrap break-all text-gray-500">{row.context.join("\n")}</pre>
                </details>
              ) : (
                <div className="whitespace-pre-wrap break-all">{fmtTime(row.at)} {row.message}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function rowBgColor(code: number | null) {
  if (!code) return "";
  if (code >= 500) return "bg-red-50 dark:bg-red-900/20";
  if (code >= 400) return "bg-amber-50 dark:bg-amber-900/20";
  return "";
}

function statusBadgeColor(code: number | null) {
  if (!code)
    return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
  if (code >= 500)
    return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
  if (code >= 400)
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
}

function SummaryCard({
  label,
  value,
  sub,
  danger,
  info,
}: {
  label: string;
  value: string | number;
  sub?: string;
  danger?: boolean;
  info?: string;
}) {
  return (
    <div
      className={clsx(
        "relative rounded-lg border p-2.5 md:p-4",
        danger && Number(value) > 0
          ? "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800"
          : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700",
      )}
    >
      {info && (
        <span className="absolute top-1.5 right-1.5">
          <HoverTooltip content={info}>
            <Info
              size={12}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help"
            />
          </HoverTooltip>
        </span>
      )}
      <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide leading-tight">
        {label}
      </p>
      <p
        className={clsx(
          "text-lg md:text-2xl font-bold mt-0.5 md:mt-1 leading-tight",
          danger && Number(value) > 0
            ? "text-red-600 dark:text-red-400"
            : "text-gray-900 dark:text-white",
        )}
      >
        {value}
      </p>
      {sub && (
        <p className="hidden md:block text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>
      )}
    </div>
  );
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${hh}:${mm}:${ss}.${ms}`;
}

function RequestRow({ r }: { r: RequestRecord }) {
  const [expanded, setExpanded] = useState(false);
  const hasError = !!r.error_body;

  return (
    <>
      <tr
        className={clsx(
          "hover:bg-gray-50 dark:hover:bg-gray-700/30",
          rowBgColor(r.status_code),
          hasError && "cursor-pointer",
        )}
        onClick={() => hasError && setExpanded((x) => !x)}
      >
        <td className="px-3 py-1.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
          {hasError ? (
            <span className="inline-flex items-center gap-1">
              {expanded ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronRight size={12} />
              )}
              {fmtTime(r.start_time)}
            </span>
          ) : (
            fmtTime(r.start_time)
          )}
        </td>
        <td
          className={clsx(
            "px-3 py-1.5 font-mono font-semibold text-xs",
            METHOD_COLORS[r.method] ?? "text-gray-600 dark:text-gray-300",
          )}
        >
          {r.method}
        </td>
        <td className="px-3 py-1.5 text-right">
          <span
            className={clsx(
              "inline-block text-xs font-semibold px-1.5 py-0.5 rounded",
              statusBadgeColor(r.status_code),
            )}
          >
            {r.status_code ?? "—"}
          </span>
        </td>
        <td
          className="px-3 py-1.5 font-mono text-xs text-gray-700 dark:text-gray-300 max-w-[180px] md:max-w-[280px] truncate"
          title={r.path}
        >
          {r.path}
        </td>
        <td
          className="hidden md:table-cell px-3 py-1.5 text-xs text-gray-400 dark:text-gray-500 max-w-[160px] truncate"
          title={r.view_name ?? ""}
        >
          {r.view_name ?? "—"}
        </td>
        <td className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
          {r.user_name ?? (
            <span className="text-gray-300 dark:text-gray-600">—</span>
          )}
        </td>
        <td className="hidden md:table-cell px-3 py-1.5 text-xs font-mono text-gray-400 dark:text-gray-500 whitespace-nowrap">
          {r.ip ?? "—"}
        </td>
        <td className="px-3 py-1.5 text-right text-xs text-gray-600 dark:text-gray-400">
          {r.time_taken != null ? Math.round(r.time_taken) : "—"}
        </td>
        <td className="hidden md:table-cell px-3 py-1.5 text-right text-xs text-gray-400 dark:text-gray-500">
          {r.num_sql_queries ?? "—"}
        </td>
      </tr>
      {expanded && r.error_body && (
        <tr className="bg-red-50 dark:bg-red-900/20">
          <td colSpan={9} className="px-4 py-2">
            <pre className="text-xs text-red-800 dark:text-red-200 whitespace-pre-wrap break-all font-mono bg-red-100 dark:bg-red-900/30 rounded p-2 max-h-48 overflow-y-auto">
              {r.error_body}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function MobileRequestRow({ r }: { r: RequestRecord }) {
  const [expanded, setExpanded] = useState(false);
  const hasError = !!r.error_body;
  return (
    <div
      className={clsx("px-3 py-2", rowBgColor(r.status_code), hasError && "cursor-pointer")}
      onClick={() => hasError && setExpanded((x) => !x)}
    >
      <div className="flex items-center gap-2">
        {hasError && (expanded ? <ChevronDown size={11} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={11} className="text-gray-400 flex-shrink-0" />)}
        <span className="text-[11px] text-gray-400 tabular-nums flex-shrink-0">{fmtTime(r.start_time)}</span>
        <span className={clsx("text-[11px] font-mono font-semibold flex-shrink-0", METHOD_COLORS[r.method] ?? "text-gray-600")}>{r.method}</span>
        <span className={clsx("text-[11px] font-semibold px-1 rounded flex-shrink-0", statusBadgeColor(r.status_code))}>
          {r.status_code ?? "—"}
        </span>
        <span className="text-[11px] text-gray-400 ml-auto tabular-nums flex-shrink-0">
          {r.time_taken != null ? `${Math.round(r.time_taken)}ms` : ""}
        </span>
      </div>
      <div className="text-[11px] font-mono text-gray-600 dark:text-gray-400 truncate mt-0.5 pl-0" title={r.path}>
        {r.path}
      </div>
      {expanded && r.error_body && (
        <pre className="text-xs text-red-800 dark:text-red-200 whitespace-pre-wrap break-all font-mono bg-red-100 dark:bg-red-900/30 rounded p-2 max-h-48 overflow-y-auto mt-1">
          {r.error_body}
        </pre>
      )}
    </div>
  );
}

function MobileCeleryRow({ t, shortName, dateStr }: { t: CeleryTaskRecord; shortName: string | null; dateStr: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <div
        className="px-3 py-2 cursor-pointer bg-red-50 dark:bg-red-900/20 flex items-center gap-2"
        onClick={() => setExpanded((x) => !x)}
      >
        {expanded ? <ChevronDown size={11} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={11} className="text-gray-400 flex-shrink-0" />}
        <span className="text-[11px] text-gray-500 flex-shrink-0 tabular-nums">{dateStr}</span>
        <span className="text-[11px] font-mono text-gray-700 dark:text-gray-300 truncate flex-1">{shortName || "—"}</span>
        <span className="text-[10px] font-semibold text-red-600 dark:text-red-400 flex-shrink-0">FAILURE</span>
      </div>
      {expanded && t.traceback && (
        <div className="px-3 pb-2 bg-red-50 dark:bg-red-900/20">
          <pre className="text-xs text-red-800 dark:text-red-200 whitespace-pre-wrap break-all font-mono bg-red-100 dark:bg-red-900/30 rounded p-2 max-h-48 overflow-y-auto">
            {t.traceback}
          </pre>
        </div>
      )}
    </>
  );
}

function CeleryFailureRow({ t }: { t: CeleryTaskRecord }) {
  const [expanded, setExpanded] = useState(false);
  const shortName = t.task_name?.split(".").slice(-2).join(".") ?? t.task_name;

  return (
    <>
      <tr
        className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer bg-red-50 dark:bg-red-900/20"
        onClick={() => setExpanded((x) => !x)}
      >
        <td className="px-3 py-1.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
          <span className="inline-flex items-center gap-1">
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {formatDateTime(t.date_done)}
          </span>
        </td>
        <td
          className="px-3 py-1.5 font-mono text-xs text-gray-700 dark:text-gray-300 max-w-[140px] truncate"
          title={t.task_name}
        >
          {shortName}
        </td>
        <td className="px-3 py-1.5">
          <span className="inline-block text-xs font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
            FAILURE
          </span>
        </td>
        <td
          className="hidden md:table-cell px-3 py-1.5 text-xs text-gray-400 dark:text-gray-500 font-mono truncate max-w-[200px]"
          title={t.task_id}
        >
          {t.task_id?.slice(0, 8)}…
        </td>
      </tr>
      {expanded && t.traceback && (
        <tr className="bg-red-50 dark:bg-red-900/20">
          <td colSpan={4} className="px-4 py-2">
            <pre className="text-xs text-red-800 dark:text-red-200 whitespace-pre-wrap break-all font-mono bg-red-100 dark:bg-red-900/30 rounded p-2 max-h-64 overflow-y-auto">
              {t.traceback}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  placeholder,
  className,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (vals: string[]) => void;
  placeholder: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click — use capture phase so it fires before React handlers
  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    // Small delay so the current click event (that opened the dropdown) doesn't
    // immediately re-trigger this handler before it propagates fully.
    const id = setTimeout(
      () => document.addEventListener("mousedown", handleMouseDown),
      0,
    );
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [open]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setSearch("");
  }, [open]);

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(search.toLowerCase()),
  );
  const toggle = (val: string) =>
    onChange(
      selected.includes(val)
        ? selected.filter((v) => v !== val)
        : [...selected, val],
    );

  return (
    <div ref={wrapperRef} className={clsx("relative ml-3", className)}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()} // prevent focus steal
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          "flex items-center gap-1.5 text-sm border rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 whitespace-nowrap",
          selected.length > 0
            ? "border-[var(--color-primary)] dark:border-[var(--color-primary)]"
            : "border-gray-200 dark:border-gray-600",
        )}
      >
        <span className="truncate max-w-[140px]">
          {selected.length === 0
            ? placeholder
            : selected.length === 1
              ? selected[0]
              : `${label}: ${selected.length}`}
        </span>
        {selected.length > 0 && (
          <span
            role="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
              setOpen(false);
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X size={12} />
          </span>
        )}
        <ChevronDown size={12} className="text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl w-64"
          onMouseDown={(e) => e.stopPropagation()} // keep dropdown open on inside clicks
        >
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj…"
              className="w-full text-sm px-2 py-1 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {options.length === 0 ? (
              <li className="px-3 py-2 text-xs text-gray-400">Ładowanie…</li>
            ) : filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-gray-400">Brak wyników</li>
            ) : (
              filtered.map((opt) => (
                <li key={opt}>
                  <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-800 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={selected.includes(opt)}
                      onChange={() => toggle(opt)}
                      className="accent-[var(--color-primary)] flex-shrink-0"
                    />
                    <span className="truncate" title={opt}>
                      {opt}
                    </span>
                  </label>
                </li>
              ))
            )}
          </ul>
          {selected.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                Odznacz wszystkie
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_COLOR: Record<TestRunStatus, string> = {
  passed: "text-emerald-400",
  failed: "text-red-400",
  error: "text-amber-400",
  running: "text-blue-400",
};
const STATUS_LABEL: Record<TestRunStatus, string> = {
  passed: "OK",
  failed: "FAILED",
  error: "ERROR",
  running: "RUNNING",
};
const DOT_COLOR: Record<TestRunStatus, string> = {
  passed: "bg-emerald-500",
  failed: "bg-red-500",
  error: "bg-[var(--bg-elevated)]0",
  running: "bg-[var(--bg-elevated)]0 animate-pulse",
};

function parseLiveTestStats(output: string) {
  const passed = (output.match(/\bPASSED\b/g) || []).length;
  const failed = (output.match(/\bFAILED\b/g) || []).length;
  const errored = (output.match(/\bERROR\b/g) || []).length;
  const progressMatches = output.match(/\[\s*(\d+)%\]/g);
  const progress = progressMatches
    ? parseInt(
        progressMatches[progressMatches.length - 1].replace(/\D/g, ""),
        10,
      )
    : null;
  return { passed, failed, errored, progress };
}

function LiveTerminal({
  output,
  isRunning,
}: {
  output: string;
  isRunning: boolean;
}) {
  const ref = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [output]);

  const { passed, failed, errored, progress } = parseLiveTestStats(output);
  const hasStats = passed > 0 || failed > 0 || errored > 0;

  return (
    <div>
      {isRunning && hasStats && (
        <div className="flex items-center gap-3 mb-2 px-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-xs font-mono">
          <span className="text-emerald-400 font-bold">{passed} passed</span>
          {failed > 0 && (
            <span className="text-red-400 font-bold">{failed} failed</span>
          )}
          {errored > 0 && (
            <span className="text-amber-400 font-bold">{errored} error</span>
          )}
          {progress !== null && (
            <span className="text-gray-500 ml-auto">{progress}% done</span>
          )}
        </div>
      )}
      <pre
        ref={ref}
        className="text-xs font-mono bg-gray-950 rounded-lg p-3 overflow-y-auto max-h-[600px] whitespace-pre-wrap leading-relaxed"
      >
        {output ? (
          <span className="text-gray-300">{output}</span>
        ) : (
          <span className="text-gray-600 italic">Czekam na output...</span>
        )}
        {isRunning && <span className="text-blue-400 animate-pulse">█</span>}
      </pre>
    </div>
  );
}

function useCoverageReport() {
  const [loading, setLoading] = useState(false);

  const open = async (runId?: string) => {
    setLoading(true);
    try {
      const report = await devMetricsApi.getCoverageReport(runId);
      const blob = new Blob([report.html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  return { open, loading };
}

function TestRunsSection({
  testRuns,
  activeDetail,
  triggeringTests,
  expandedRunId,
  runningRunId,
  onTrigger,
  onToggleExpand,
  onAbort,
}: {
  testRuns: TestRunResult[];
  activeDetail: TestRunDetail | undefined;
  triggeringTests: boolean;
  expandedRunId: string | null;
  runningRunId: string | null;
  onTrigger: () => void;
  onToggleExpand: (id: string) => void;
  onAbort: (id: string) => void;
}) {
  const coverage = useCoverageReport();
  const latestWithCoverage = testRuns.find((r) => r.coverage_s3_prefix);

  return (
    <section className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-100">
            Testy jednostkowe
          </span>
          <span className="text-xs text-gray-500">codziennie o 20:00 automatycznie</span>
          {runningRunId && (
            <span className="text-xs text-blue-400 animate-pulse font-medium">
              ● trwa...
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {latestWithCoverage && (
            <button
              onClick={() => coverage.open(latestWithCoverage.id)}
              disabled={coverage.loading}
              title="Pokrycie kodu"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-violet-900/40 hover:bg-violet-800/50 text-violet-300 disabled:opacity-50 transition-colors"
            >
              <BarChart2
                size={12}
                className={coverage.loading ? "animate-pulse" : ""}
              />
              <span className="hidden sm:inline">Pokrycie kodu</span>
            </button>
          )}
          {runningRunId && (
            <button
              onClick={() => onAbort(runningRunId)}
              title="Przerwij"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-red-900/50 hover:bg-red-800/60 text-red-300 transition-colors"
            >
              <X size={12} />
              <span className="hidden sm:inline">Przerwij</span>
            </button>
          )}
          <button
            disabled={triggeringTests || !!runningRunId}
            onClick={onTrigger}
            title="Uruchom teraz"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-50 transition-colors"
          >
            <RefreshCw
              size={12}
              className={triggeringTests ? "animate-spin" : ""}
            />
            <span className="hidden sm:inline">Uruchom teraz</span>
          </button>
        </div>
      </div>

      {testRuns.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-500">
          Brak wyników — testy jeszcze nie były uruchamiane.
        </div>
      ) : (
        <div className="divide-y divide-gray-800">
          {testRuns.map((run) => {
            const isRunning = run.status === "running";
            const isExpanded = isRunning || expandedRunId === run.id;
            const timeLabel = formatDateTime(run.started_at);
            const isActiveDetail = activeDetail?.id === run.id;
            const liveStats =
              isRunning && isActiveDetail
                ? parseLiveTestStats(activeDetail?.output ?? "")
                : null;

            return (
              <div key={run.id}>
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-800/50 transition-colors text-left"
                  onClick={() => !isRunning && onToggleExpand(run.id)}
                >
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${DOT_COLOR[run.status]}`}
                  />
                  <span
                    className={`text-xs font-bold w-12 md:w-16 flex-shrink-0 ${STATUS_COLOR[run.status]}`}
                  >
                    {STATUS_LABEL[run.status]}
                  </span>
                  <span className="text-xs text-gray-400 w-28 md:w-32 flex-shrink-0 tabular-nums">
                    {timeLabel}
                  </span>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {run.triggered_by === "manual" ? "ręczne" : "auto"}
                  </span>
                  {isRunning &&
                    liveStats &&
                    (liveStats.passed > 0 ||
                      liveStats.failed > 0 ||
                      liveStats.errored > 0) && (
                      <span className="flex items-center gap-2 text-xs font-mono flex-shrink-0">
                        <span className="text-emerald-400 font-semibold">
                          {liveStats.passed}✓
                        </span>
                        {liveStats.failed > 0 && (
                          <span className="text-red-400 font-semibold">
                            {liveStats.failed}✗
                          </span>
                        )}
                        {liveStats.errored > 0 && (
                          <span className="text-amber-400 font-semibold">
                            {liveStats.errored}!
                          </span>
                        )}
                        {liveStats.progress !== null && (
                          <span className="text-gray-600">
                            {liveStats.progress}%
                          </span>
                        )}
                      </span>
                    )}
                  {!isRunning && (
                    <span className="hidden sm:inline text-xs text-gray-500 ml-1">
                      <span className="text-emerald-400">{run.passed}</span>
                      <span className="text-gray-600">/{run.total}</span>
                      {run.failed > 0 && (
                        <span className="text-red-400 ml-1">
                          {run.failed} failed
                        </span>
                      )}
                      {run.errored > 0 && (
                        <span className="text-amber-400 ml-1">
                          {run.errored} err
                        </span>
                      )}
                      {run.duration_seconds != null && (
                        <span className="ml-2 text-gray-600">
                          {run.duration_seconds.toFixed(1)}s
                        </span>
                      )}
                    </span>
                  )}
                  {!isRunning && run.coverage_s3_prefix && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        coverage.open(run.id);
                      }}
                      disabled={coverage.loading}
                      title="Otwórz raport pokrycia"
                      className="ml-auto mr-1 p-1 rounded text-violet-500 hover:text-violet-300 hover:bg-violet-900/30 transition-colors disabled:opacity-50"
                    >
                      <BarChart2 size={13} />
                    </button>
                  )}
                  {!isRunning && (
                    <span
                      className={run.coverage_s3_prefix ? "" : "ml-auto"}
                      style={{ color: "var(--color-gray-600)" }}
                    >
                      {isExpanded ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                    </span>
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-3">
                    {isRunning ? (
                      <LiveTerminal
                        output={
                          isActiveDetail ? (activeDetail?.output ?? "") : ""
                        }
                        isRunning
                      />
                    ) : isActiveDetail && activeDetail?.failure_summary ? (
                      <pre className="text-xs text-red-300 bg-gray-950 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap font-mono max-h-[600px] overflow-y-auto">
                        {activeDetail.failure_summary}
                      </pre>
                    ) : run.failure_summary ? (
                      <pre className="text-xs text-red-300 bg-gray-950 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap font-mono max-h-[600px] overflow-y-auto">
                        {run.failure_summary}
                      </pre>
                    ) : (
                      <p className="text-xs text-gray-500 italic">
                        Brak błędów do wyświetlenia.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function DevMetricsPage() {
  const user = useAuthStore((s) => s.user);
  const isDevOrDirector = !!(user?.is_developer || user?.role === "director");
  const qc = useQueryClient();
  const [triggeringTests, setTriggeringTests] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["dev-metrics-meta"] });
    qc.invalidateQueries({ queryKey: ["dev-metrics-requests"] });
    qc.invalidateQueries({ queryKey: ["dev-metrics-timeseries"] });
  };

  const [resettingSilk, setResettingSilk] = useState(false);
  const handleSilkReset = async () => {
    if (
      !window.confirm(
        "Usunąć wszystkie dane Silk (historia requestów)? Operacja nieodwracalna.",
      )
    )
      return;
    setResettingSilk(true);
    try {
      await devMetricsApi.resetSilk();
      refetchAll();
    } finally {
      setResettingSilk(false);
    }
  };

  // ── Pending (UI controls) vs Applied (sent to API) filter state ──
  const [pendingMethod, setPendingMethod] = useState<string>("all");
  const [pendingStatus, setPendingStatus] = useState<string>("all");
  const [pendingMinMs, setPendingMinMs] = useState<number>(0);
  const [pendingUsers, setPendingUsers] = useState<string[]>([]);
  const [pendingPaths, setPendingPaths] = useState<string[]>([]);

  const [appliedMethod, setAppliedMethod] = useState<string>("all");
  const [appliedStatus, setAppliedStatus] = useState<string>("all");
  const [appliedMinMs, setAppliedMinMs] = useState<number>(0);
  const [appliedUsers, setAppliedUsers] = useState<string[]>([]);
  const [appliedPaths, setAppliedPaths] = useState<string[]>([]);

  const pendingUserStr = pendingUsers.join("|");
  const pendingPathStr = pendingPaths.join("|");
  const appliedUserStr = appliedUsers.join("|");
  const appliedPathStr = appliedPaths.join("|");

  const hasPending =
    pendingMethod !== appliedMethod ||
    pendingStatus !== appliedStatus ||
    pendingMinMs !== appliedMinMs ||
    pendingUserStr !== appliedUserStr ||
    pendingPathStr !== appliedPathStr;

  function applyFilters() {
    setAppliedMethod(pendingMethod);
    setAppliedStatus(pendingStatus);
    setAppliedMinMs(pendingMinMs);
    setAppliedUsers(pendingUsers);
    setAppliedPaths(pendingPaths);
  }

  const [endpointSort, setEndpointSort] = useState<{
    key: keyof EndpointStat;
    dir: "asc" | "desc";
  }>({ key: "count", dir: "desc" });

  // ── Meta (summary, celery, endpoints) — always unfiltered, refreshes every 30s
  const {
    data: metaData,
    dataUpdatedAt,
    refetch: refetchMeta,
    isFetching: isFetchingMeta,
  } = useQuery({
    queryKey: ["dev-metrics-meta"],
    queryFn: devMetricsApi.get,
    refetchInterval: 30_000,
    enabled: isDevOrDirector,
  });

  // ── Celery failures — infinite scroll, cursor-paginated ──
  const celeryScrollRef = useRef<HTMLDivElement>(null);
  const {
    data: celeryFailuresData,
    fetchNextPage: fetchNextCeleryPage,
    hasNextPage: celeryHasNextPage,
    isFetchingNextPage: celeryFetchingNext,
  } = useInfiniteQuery<CeleryFailuresPage>({
    queryKey: ["dev-metrics-celery-failures"],
    queryFn: ({ pageParam }) =>
      devMetricsApi.getCeleryFailures(pageParam as string | undefined),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    initialPageParam: undefined,
    enabled: isDevOrDirector,
  });

  useEffect(() => {
    const el = celeryScrollRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          celeryHasNextPage &&
          !celeryFetchingNext
        ) {
          fetchNextCeleryPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [celeryHasNextPage, celeryFetchingNext, fetchNextCeleryPage]);

  const celeryFailures =
    celeryFailuresData?.pages.flatMap((p) => p.failures) ?? [];

  // ── All system users for the filter dropdown ──
  const { data: allUsers = [] } = useQuery({
    queryKey: ["dev-metrics-users"],
    queryFn: devMetricsApi.getUsers,
    staleTime: 1000 * 60 * 5,
    enabled: isDevOrDirector,
  });

  // ── Test run results ──
  const { data: testRuns = [], refetch: refetchTestRuns } = useQuery<
    TestRunResult[]
  >({
    queryKey: ["dev-metrics-test-runs"],
    queryFn: devMetricsApi.getTestRuns,
    refetchInterval: (query) => {
      const runs = query.state.data as TestRunResult[] | undefined;
      return runs?.some((r) => r.status === "running") ? 2_000 : 15_000;
    },
    enabled: isDevOrDirector,
  });

  const runningRun = testRuns.find((r) => r.status === "running");
  const detailId = runningRun?.id ?? expandedRunId ?? undefined;

  const { data: activeDetail } = useQuery<TestRunDetail>({
    queryKey: ["dev-metrics-test-run-detail", detailId],
    queryFn: () => devMetricsApi.getTestRunDetail(detailId!),
    enabled: !!detailId && isDevOrDirector,
    refetchInterval: runningRun ? 2_000 : false,
  });

  // ── Filtered, paginated requests — only re-fetches when APPLIED filters change ──
  const activeFilters = {
    method: appliedMethod !== "all" ? appliedMethod : undefined,
    status: appliedStatus !== "all" ? appliedStatus : undefined,
    path: appliedPathStr || undefined,
    user: appliedUserStr || undefined,
    min_ms: appliedMinMs > 0 ? appliedMinMs : undefined,
  };

  const {
    data: reqData,
    isLoading: reqLoading,
    error: reqError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "dev-metrics-requests",
      appliedMethod,
      appliedStatus,
      appliedPathStr,
      appliedUserStr,
      appliedMinMs,
    ],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      devMetricsApi.getRequests(pageParam, activeFilters),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    refetchInterval: 30_000,
    enabled: isDevOrDirector,
  });

  if (!isDevOrDirector) return <Navigate to="/" replace />;

  const silk = metaData?.silk ?? null;
  const celery = metaData?.celery ?? null;
  const requests: RequestRecord[] =
    reqData?.pages.flatMap((p) => p.requests) ?? [];
  const isLoading = reqLoading && !metaData;
  const error = reqError;

  const endpoints: EndpointStat[] = silk?.endpoints ?? [];

  const pathOptions = useMemo(
    () => endpoints.map((e) => e.example_path).sort(),
    [endpoints],
  );

  // Max cumulative time across endpoints — used to scale the "Łączny czas"
  // heat colouring so the biggest time-sink stands out at a glance.
  const maxTotalTime = useMemo(
    () => endpoints.reduce((m, e) => Math.max(m, e.total_time_ms ?? 0), 0),
    [endpoints],
  );

  const sortedEndpoints = [...endpoints].sort((a, b) => {
    const av = a[endpointSort.key] ?? "";
    const bv = b[endpointSort.key] ?? "";
    if (typeof av === "string" && typeof bv === "string") {
      return endpointSort.dir === "desc"
        ? bv.localeCompare(av)
        : av.localeCompare(bv);
    }
    return endpointSort.dir === "desc"
      ? (bv as number) - (av as number)
      : (av as number) - (bv as number);
  });
  function toggleEndpointSort(key: keyof EndpointStat) {
    setEndpointSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "desc" ? "asc" : "desc" }
        : { key, dir: "desc" },
    );
  }
  function SortIcon({ col }: { col: keyof EndpointStat }) {
    if (endpointSort.key !== col) return null;
    return endpointSort.dir === "desc" ? (
      <ArrowDown size={11} className="inline ml-0.5" />
    ) : (
      <ArrowUp size={11} className="inline ml-0.5" />
    );
  }
  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString("pl-PL")
    : "—";

  return (
    <div className="px-3 py-4 md:p-6 max-w-7xl mx-auto space-y-6 md:space-y-8">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Metryki systemu
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Ostatnie 24h · odświeżanie co 30s · ostatnio: {lastUpdated}
          </p>
        </div>
        <button
          onClick={refetchAll}
          title="Odśwież dane"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <RefreshCw size={14} />
          Odśwież
        </button>
      </div>

      {/* ── Timeseries charts ── */}
      <MetricsCharts enabled={isDevOrDirector} />

      {/* ── Collabora (edytor pism) — stan na teraz ── */}
      <CollaboraStatusSection enabled={isDevOrDirector} />

      {/* ── Błędy serwera z logu web — czego Silk nie widzi ── */}
      <WebErrorLogSection enabled={isDevOrDirector} />

      {/* ── HTTP summary ── */}
      <section>
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-3">
          HTTP Requests (24h)
        </h2>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-4">
          <SummaryCard
            label="Requesty"
            value={silk?.summary?.total_24h ?? "—"}
          />
          <SummaryCard
            label="Śr. czas"
            value={silk?.summary ? `${silk.summary.avg_time_ms} ms` : "—"}
          />
          <SummaryCard
            label="Łączny czas"
            value={
              silk?.summary ? fmtDuration(silk.summary.total_time_ms) : "—"
            }
            sub="liczba × śr. czas"
            info="Sumaryczny czas obsługi wszystkich requestów w oknie 24h (liczba requestów × średni czas)."
          />
          <SummaryCard
            label="P95 czas"
            value={silk?.summary ? `${silk.summary.p95_time_ms} ms` : "—"}
            sub="95. percentyl"
          />
          <SummaryCard
            label="Błędy 4xx"
            value={silk?.summary?.error_4xx ?? "—"}
            danger
          />
          <SummaryCard
            label="Błędy 5xx"
            value={silk?.summary?.error_5xx ?? "—"}
            danger
          />
        </div>
      </section>

      {/* ── Celery summary ── */}
      <section>
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-3">
          Celery Tasks (24h)
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
          <SummaryCard
            label="Wykonane"
            value={celery?.summary?.total_24h ?? "—"}
          />
          <SummaryCard
            label="Zakończone sukcesem"
            value={celery?.summary?.success_24h ?? "—"}
          />
          <SummaryCard
            label="Błędy"
            value={celery?.summary?.failure_24h ?? "—"}
            danger
          />
          <SummaryCard
            label="Workery (teraz)"
            value={celery?.running_workers ?? "—"}
            info={
              "Realna liczba działających kontenerów workera Celery " +
              "(ECS RunningTaskCount z CloudWatch, ~1–2 min opóźnienia — " +
              "wartość zmierzona, nie z harmonogramu).\n\n" +
              "Autoskalowanie: 2 kontenery w dni robocze 07:00–17:00, " +
              "1 poza godzinami i w weekendy.\n" +
              "Każdy kontener obsługuje 2 zadania równolegle (--concurrency=2)."
            }
          />
        </div>

        {celeryFailures.length > 0 && (
          <div className="mt-4">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Ostatnie błędy Celery
              </p>
            </div>
            {/* Mobile: card list */}
            <div className="md:hidden bg-white dark:bg-gray-800 divide-y divide-red-50 dark:divide-gray-700/50 max-h-72 overflow-y-auto">
              {celeryFailures.map((t) => {
                const shortName = t.task_name?.split(".").slice(-2).join(".") ?? t.task_name;
                const dateStr = formatDateTime(t.date_done);
                return (
                  <MobileCeleryRow key={t.task_id} t={t} shortName={shortName} dateStr={dateStr} />
                );
              })}
              <div ref={celeryScrollRef} className="h-4">
                {celeryFetchingNext && <div className="flex justify-center py-1"><Spinner size="sm" /></div>}
              </div>
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block bg-white dark:bg-gray-800 overflow-x-auto overflow-y-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-white dark:bg-gray-800">
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest">Czas</th>
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest">Task</th>
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest">Status</th>
                    <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest">ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {celeryFailures.map((t) => <CeleryFailureRow key={t.task_id} t={t} />)}
                </tbody>
              </table>
              <div ref={celeryScrollRef} className="h-4">
                {celeryFetchingNext && <div className="flex justify-center py-1"><Spinner size="sm" /></div>}
              </div>
            </div>
          </div>
        )}
        {celery?.error && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
            Celery: {celery.error} — uruchom{" "}
            <code>docker compose up --build</code> aby załadować nowy result
            backend
          </p>
        )}
      </section>

      {/* ── Request table ── */}
      <section>
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-3">
          Ostatnie requesty
        </h2>
        <div className="bg-white dark:bg-gray-800">
          {/* Mobile filters */}
          <div className="md:hidden px-3 py-2 border-b border-gray-100 dark:border-gray-700 space-y-2">
            <div className="grid grid-cols-3 gap-1.5">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-gray-400 uppercase tracking-wide">Metoda</span>
                <select
                  value={pendingMethod}
                  onChange={(e) => setPendingMethod(e.target.value)}
                  className="text-xs rounded px-1.5 py-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600"
                >
                  <option value="all">Wszystkie</option>
                  {["GET", "POST", "PATCH", "PUT", "DELETE", "HEAD", "OPTIONS"].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-gray-400 uppercase tracking-wide">Status</span>
                <select
                  value={pendingStatus}
                  onChange={(e) => setPendingStatus(e.target.value)}
                  className="text-xs rounded px-1.5 py-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600"
                >
                  <option value="all">Wszystkie</option>
                  <option value="2xx">2xx</option>
                  <option value="4xx">4xx</option>
                  <option value="5xx">5xx</option>
                  <option value="errors">Błędy</option>
                </select>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-gray-400 uppercase tracking-wide">Czas min.</span>
                <select
                  value={pendingMinMs}
                  onChange={(e) => setPendingMinMs(Number(e.target.value))}
                  className="text-xs rounded px-1.5 py-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600"
                >
                  <option value={0}>Wszyst.</option>
                  {[100, 500, 1000, 3000, 5000, 10000].map((ms) => (
                    <option key={ms} value={ms}>{ms >= 1000 ? `${ms / 1000}s+` : `${ms}ms+`}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-1.5 items-center">
              <MultiSelectDropdown
                label="Użytkownicy"
                options={allUsers}
                selected={pendingUsers}
                onChange={setPendingUsers}
                placeholder="Użytkownik…"
                className="ml-0 flex-1 min-w-0"
              />
              <MultiSelectDropdown
                label="Ścieżki"
                options={pathOptions}
                selected={pendingPaths}
                onChange={setPendingPaths}
                placeholder="Ścieżka…"
                className="ml-0 flex-1 min-w-0"
              />
              {hasPending && (
                <button
                  onClick={applyFilters}
                  className="px-2.5 py-1 text-xs font-medium rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white transition-colors flex-shrink-0"
                >
                  Załaduj
                </button>
              )}
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {requests.length} załadowanych{hasNextPage && " · więcej"}
            </span>
          </div>

          {/* Desktop filters */}
          <div className="hidden md:flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-gray-500 dark:text-gray-400">Metoda:</span>
              <select
                value={pendingMethod}
                onChange={(e) => setPendingMethod(e.target.value)}
                className="text-sm rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              >
                <option value="all">Wszystkie</option>
                {["GET", "POST", "PATCH", "PUT", "DELETE", "HEAD", "OPTIONS"].map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-gray-500 dark:text-gray-400">Status:</span>
              <select
                value={pendingStatus}
                onChange={(e) => setPendingStatus(e.target.value)}
                className="text-sm rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              >
                <option value="all">Wszystkie</option>
                <option value="2xx">2xx</option>
                <option value="4xx">4xx</option>
                <option value="5xx">5xx</option>
                <option value="errors">Błędy (4xx+5xx)</option>
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-gray-500 dark:text-gray-400">Czas:</span>
              <select
                value={pendingMinMs}
                onChange={(e) => setPendingMinMs(Number(e.target.value))}
                className="text-sm rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              >
                <option value={0}>Wszystkie</option>
                {[100, 500, 1000, 3000, 5000, 10000].map((ms) => (
                  <option key={ms} value={ms}>{ms >= 1000 ? `${ms / 1000}s+` : `${ms}ms+`}</option>
                ))}
              </select>
            </div>
            <MultiSelectDropdown label="Użytkownicy" options={allUsers} selected={pendingUsers} onChange={setPendingUsers} placeholder="Filtruj użytkownika…" />
            <MultiSelectDropdown label="Ścieżki" options={pathOptions} selected={pendingPaths} onChange={setPendingPaths} placeholder="Filtruj ścieżkę…" />
            {hasPending && (
              <button onClick={applyFilters} className="px-3 py-1 text-sm font-medium rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white transition-colors">
                Załaduj
              </button>
            )}
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
              {requests.length} załadowanych
              {hasNextPage && <span className="text-gray-300 dark:text-gray-600"> · więcej dostępne</span>}
            </span>
          </div>

          {isLoading && (
            <div className="p-8 text-center text-sm text-gray-400">
              Ładowanie…
            </div>
          )}
          {error && (
            <div className="p-8 text-center text-sm text-red-500">
              Błąd: {String(error)}
            </div>
          )}

          {!isLoading && !error && (
            <>
              {/* Mobile: card list */}
              <div className="md:hidden divide-y divide-gray-50 dark:divide-gray-700/50 max-h-[36rem] overflow-y-auto">
                {requests.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-gray-400">Brak danych</p>
                ) : (
                  requests.map((r, i) => <MobileRequestRow key={i} r={r} />)
                )}
                {(hasNextPage || isFetchingNextPage) && (
                  <div className="px-4 py-2 text-center">
                    <button
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className="text-sm text-[var(--color-primary)] disabled:opacity-50 font-medium py-1"
                    >
                      {isFetchingNextPage ? "Ładowanie…" : "Załaduj starsze"}
                    </button>
                  </div>
                )}
              </div>

              {/* Desktop: full table */}
              <div className="hidden md:block overflow-auto max-h-[36rem]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white dark:bg-gray-800 z-10">
                    <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest">Czas</th>
                      <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest">Metoda</th>
                      <th className="px-3 py-2 text-right font-mono text-xs uppercase tracking-widest">Status</th>
                      <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest">Ścieżka</th>
                      <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest">Widok</th>
                      <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest">Użytkownik</th>
                      <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest">IP</th>
                      <th className="px-3 py-2 text-right font-mono text-xs uppercase tracking-widest">ms</th>
                      <th className="px-3 py-2 text-right font-mono text-xs uppercase tracking-widest">SQL</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                    {requests.map((r, i) => <RequestRow key={i} r={r} />)}
                    {requests.length === 0 && (
                      <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-400">Brak danych</td></tr>
                    )}
                    {(hasNextPage || isFetchingNextPage) && (
                      <tr>
                        <td colSpan={9} className="px-4 py-2 text-center">
                          <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
                            className="text-sm text-[var(--color-primary)] disabled:opacity-50 font-medium py-1">
                            {isFetchingNextPage ? "Ładowanie…" : "Załaduj starsze"}
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── Per-endpoint analysis ── */}
      {endpoints.length > 0 && (
        <section>
          <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                Analiza per endpoint (24h)
              </h2>
              <button
                onClick={() => refetchMeta()}
                disabled={isFetchingMeta}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-40 transition-colors"
                title="Odśwież"
              >
                <RefreshCw
                  size={13}
                  className={isFetchingMeta ? "animate-spin" : ""}
                />
              </button>
            </div>
            {user?.is_developer && (
              <button
                onClick={handleSilkReset}
                disabled={resettingSilk}
                className="text-xs px-2.5 py-1 rounded border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 transition-colors"
              >
                {resettingSilk ? "Resetowanie..." : "Resetuj dane requestów"}
              </button>
            )}
          </div>
          {/* Mobile: card list */}
          <div className="md:hidden bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700/50 max-h-[36rem] overflow-y-auto">
            {sortedEndpoints.map((e, i) => (
              <div key={i} className={clsx("px-3 py-2", i % 2 !== 0 && "bg-gray-50/60 dark:bg-gray-700/20")}>
                <div className="flex items-center gap-2">
                  <span className={clsx("text-[11px] font-mono font-semibold flex-shrink-0", METHOD_COLORS[e.method] ?? "text-gray-600")}>
                    {e.method || "?"}
                  </span>
                  <span className="text-[11px] font-mono text-gray-700 dark:text-gray-300 truncate flex-1" title={e.view_name}>
                    {e.view_name}
                  </span>
                  <span className="text-[11px] tabular-nums text-gray-500 flex-shrink-0 font-medium">
                    {e.count.toLocaleString("pl-PL")}×
                  </span>
                </div>
                <div className="text-[10px] font-mono text-gray-400 truncate mt-0.5" title={e.example_path}>
                  {e.example_path}
                </div>
                <div className="flex gap-3 text-[10px] text-gray-400 mt-0.5">
                  {e.avg_time_ms != null && (
                    <span className={e.avg_time_ms > 500 ? "text-amber-600 dark:text-amber-400" : ""}>
                      śr. {e.avg_time_ms}ms
                    </span>
                  )}
                  {e.p95_time_ms != null && (
                    <span className={e.p95_time_ms > 1000 ? "text-red-600 dark:text-red-400" : ""}>
                      p95 {e.p95_time_ms}ms
                    </span>
                  )}
                  <span>łącznie {fmtDuration(e.total_time_ms)}</span>
                  {e.error_rate > 0 && (
                    <span className={e.error_rate > 10 ? "text-red-600 dark:text-red-400 font-medium" : "text-amber-600 dark:text-amber-400"}>
                      {e.error_rate}% błędów
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: full table */}
          <div className="hidden md:block bg-white dark:bg-gray-800 overflow-auto max-h-[36rem]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white dark:bg-gray-800 z-10 border-b border-gray-100 dark:border-gray-700">
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                  <th
                    className="px-3 py-2 cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                    onClick={() => toggleEndpointSort("method")}
                  >
                    Metoda <SortIcon col="method" />
                  </th>
                  <th
                    className="px-3 py-2 cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                    onClick={() => toggleEndpointSort("view_name")}
                  >
                    Endpoint <SortIcon col="view_name" />
                  </th>
                  <th
                    className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                    onClick={() => toggleEndpointSort("count")}
                  >
                    Requesty <SortIcon col="count" />
                  </th>
                  <th
                    className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                    onClick={() => toggleEndpointSort("avg_time_ms")}
                  >
                    Śr. czas <SortIcon col="avg_time_ms" />
                  </th>
                  <th
                    className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                    onClick={() => toggleEndpointSort("total_time_ms")}
                    title="Łączny czas obsługi = liczba requestów × średni czas"
                  >
                    Łączny czas <SortIcon col="total_time_ms" />
                  </th>
                  <th
                    className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                    onClick={() => toggleEndpointSort("p95_time_ms")}
                  >
                    P95 <SortIcon col="p95_time_ms" />
                  </th>
                  <th
                    className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                    onClick={() => toggleEndpointSort("error_rate")}
                  >
                    Błędy % <SortIcon col="error_rate" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {sortedEndpoints.map((e, i) => (
                  <tr
                    key={i}
                    className={clsx(
                      i % 2 === 0
                        ? "bg-white dark:bg-gray-800"
                        : "bg-gray-50/60 dark:bg-gray-700/20",
                      "hover:bg-blue-50/40 dark:hover:bg-gray-700/40 transition-colors",
                    )}
                  >
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <span
                        className={clsx(
                          "font-mono text-xs font-semibold",
                          METHOD_COLORS[e.method] ??
                            "text-gray-600 dark:text-gray-300",
                        )}
                      >
                        {e.method || "?"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">
                      <div
                        className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate max-w-[380px]"
                        title={e.view_name}
                      >
                        {e.view_name}
                      </div>
                      <div
                        className="font-mono text-xs text-gray-400 dark:text-gray-500 truncate max-w-[380px]"
                        title={e.example_path}
                      >
                        {e.example_path}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">
                      {e.count.toLocaleString("pl-PL")}
                    </td>
                    <td className="px-3 py-1.5 text-right text-sm tabular-nums">
                      <span
                        className={clsx(
                          e.avg_time_ms != null && e.avg_time_ms > 500
                            ? "text-amber-600 dark:text-amber-400 font-medium"
                            : "text-gray-600 dark:text-gray-400",
                        )}
                      >
                        {e.avg_time_ms != null ? `${e.avg_time_ms} ms` : "—"}
                      </span>
                    </td>
                    <td
                      className="px-3 py-1.5 text-right text-sm tabular-nums font-medium text-gray-700 dark:text-gray-200"
                      style={{ backgroundColor: heatBg(e.total_time_ms, maxTotalTime) }}
                    >
                      {fmtDuration(e.total_time_ms)}
                    </td>
                    <td className="px-3 py-1.5 text-right text-sm tabular-nums">
                      <span
                        className={clsx(
                          e.p95_time_ms != null && e.p95_time_ms > 1000
                            ? "text-red-600 dark:text-red-400 font-medium"
                            : "text-gray-600 dark:text-gray-400",
                        )}
                      >
                        {e.p95_time_ms != null ? `${e.p95_time_ms} ms` : "—"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right text-sm tabular-nums">
                      <span
                        className={clsx(
                          e.error_rate > 10
                            ? "text-red-600 dark:text-red-400 font-medium"
                            : e.error_rate > 0
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-gray-400 dark:text-gray-500",
                        )}
                      >
                        {e.error_rate > 0 ? `${e.error_rate}%` : "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Test Runs ── */}
      <TestRunsSection
        testRuns={testRuns}
        activeDetail={activeDetail}
        triggeringTests={triggeringTests}
        expandedRunId={expandedRunId}
        runningRunId={runningRun?.id ?? null}
        onTrigger={async () => {
          setTriggeringTests(true);
          try {
            await devMetricsApi.triggerTestRun();
            setTimeout(() => refetchTestRuns(), 1500);
          } finally {
            setTriggeringTests(false);
          }
        }}
        onToggleExpand={(id) =>
          setExpandedRunId((prev) => (prev === id ? null : id))
        }
        onAbort={async (id) => {
          await devMetricsApi.abortTestRun(id);
          refetchTestRuns();
        }}
      />

      {/* ── QGIS perf benchmark ── */}
      <PerfRunsSection />
    </div>
  );
}
